import {
  create_land_court_journal,
  fetch_land_court_application_forms,
  fetch_land_court_application_documents,
  fetch_land_court_journals,
  submit_land_court_form_answers,
  update_land_court_journal,
  upload_land_court_additional_documents,
  verify_land_court_application,
} from '@/services/land_court_service';
import {
  fetch_assign_form_status_data,
  fetch_form_workflow_transition_rules,
} from '@/services/workflow_service';
import alert_mixin from '@/utils/mixins/alert_mixin';

const CASE_META_STORAGE_KEY = 'land_court_case_meta';
const CASELOAD_SNAPSHOT_STORAGE_PREFIX = 'land_court_caseload_snapshot:';

function clear_caseload_snapshots() {
  if (typeof window === 'undefined') {
    return;
  }

  const storage_keys = [];
  for (let index = 0; index < window.sessionStorage.length; index += 1) {
    const storage_key = window.sessionStorage.key(index);
    if (storage_key?.startsWith(CASELOAD_SNAPSHOT_STORAGE_PREFIX)) {
      storage_keys.push(storage_key);
    }
  }

  storage_keys.forEach((storage_key) => {
    window.sessionStorage.removeItem(storage_key);
  });
}

const CASE_DATA_FIELD_KEYS = Object.freeze([
  'known_or_registered_farm',
  'province',
  'town_district',
  'municipality',
  'select_from_existing_farms',
]);

const CASE_DATA_FIELD_CONFIG = Object.freeze({
  known_or_registered_farm: {
    label: 'Known or registered farm',
    labels: ['known or registered farm', 'registered farm', 'known farm'],
    type: 'text',
  },
  province: {
    label: 'Province',
    labels: ['province'],
    type: 'text',
  },
  town_district: {
    label: 'Town/District',
    labels: ['town district', 'town/district', 'district', 'town'],
    type: 'text',
  },
  municipality: {
    label: 'Municipality',
    labels: ['municipality'],
    type: 'text',
  },
  select_from_existing_farms: {
    label: 'Select from existing farms',
    labels: ['select from existing farms', 'existing farms', 'existing farm'],
    type: 'select',
    options: [],
  },
});

const CASE_DATA_FIELD_ACCESSORS = Object.freeze([
  {
    key: 'known_or_registered_farm',
    read_value: (source = {}) => source?.known_or_registered_farm,
    read_question: (source = {}) => source?.known_or_registered_farm,
  },
  {
    key: 'province',
    read_value: (source = {}) => source?.province,
    read_question: (source = {}) => source?.province,
  },
  {
    key: 'town_district',
    read_value: (source = {}) => source?.town_district,
    read_question: (source = {}) => source?.town_district,
  },
  {
    key: 'municipality',
    read_value: (source = {}) => source?.municipality,
    read_question: (source = {}) => source?.municipality,
  },
  {
    key: 'select_from_existing_farms',
    read_value: (source = {}) => source?.select_from_existing_farms,
    read_question: (source = {}) => source?.select_from_existing_farms,
  },
]);

const PARTY_FIELD_KEYS = Object.freeze([
  'applicants',
  'applicants_legal_representatives',
  'respondents',
  'respondents_legal_representatives',
]);

const PARTY_FIELD_CONFIG = Object.freeze({
  applicants: {
    labels: ['applicants', 'applicant'],
  },
  applicants_legal_representatives: {
    labels: ["applicants' legal representatives"],
  },
  respondents: {
    labels: ['respondents', 'respondent'],
  },
  respondents_legal_representatives: {
    labels: ["landowner's legal representatives"],
  },
});

const LEGAL_REPRESENTATIVE_FIELD_KEYS = Object.freeze([
  'applicants_legal_representatives',
  'respondents_legal_representatives',
]);

const REPRESENTATIVE_VERIFICATION_FIELD_BY_REPRESENTATIVE_FIELD = Object.freeze({
  applicants_legal_representatives: 'applicants_legal_representatives_verified',
  respondents_legal_representatives: 'respondents_legal_representatives_verified',
});

const LEGAL_REPRESENTATIVE_VERIFICATION_OPTIONS = Object.freeze(['Not verified', 'Verified']);
const DEFAULT_LEGAL_REPRESENTATIVE_VERIFICATION = 'Not verified';
const REPRESENTATIVE_VERIFICATION_LABEL = 'Representative Details';
const REPRESENTATIVE_VERIFICATION_PATTERN =
  /(?:Representative Details|Representative details verified)\s*:\s*(Verified|Not verified)/i;
const REPRESENTATIVE_DETAIL_FIELDS = Object.freeze([
  { key: 'firm_name', label: 'Name' },
  { key: 'contact_person', label: 'Contact person' },
  { key: 'address', label: 'Address' },
  { key: 'telephone_number', label: 'Telephone number' },
]);

const PARTY_FIELD_DEFAULT_VALUES = Object.freeze({
  applicants: '',
  applicants_legal_representatives: [build_empty_representative_detail()],
  applicants_legal_representatives_verified: [DEFAULT_LEGAL_REPRESENTATIVE_VERIFICATION],
  respondents: '',
  respondents_legal_representatives: [build_empty_representative_detail()],
  respondents_legal_representatives_verified: [DEFAULT_LEGAL_REPRESENTATIVE_VERIFICATION],
});

const FILING_MARKER = 'LAND_COURT_FILING::';
const FILING_ACTOR_OPTIONS = ['Applicants', 'Respondent', 'Court', 'Other'];
const MATTER_MARKER = 'LAND_COURT_INTERLOCUTORY_MATTER::';
const MATTER_STATUS_OPTIONS = ['Pending', 'Resolved', 'Stuck'];
const JUDGE_NOTE_MARKER = 'LAND_COURT_JUDGE_NOTE::';
const FURTHER_ACTION_MARKER = 'LAND_COURT_FURTHER_ACTION::';
const FURTHER_ACTION_STATUS_OPTIONS = ['Pending', 'Urgent', 'Resolved'];
const DOCUMENT_CATEGORY_OPTIONS = ['Pleading', 'Affidavit', 'Notice', 'Correspondence', 'Exhibit', 'Other'];
const DOCUMENT_PARTY_OPTIONS = ['Applicants', 'Respondent', 'Court', 'Other'];
const ALLOWED_DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx', 'jpg', 'jpeg'];
const MAX_DOCUMENT_SIZE_BYTES = 50 * 1024 * 1024;

function normalize_text(value) {
  return String(value || '').trim();
}

function build_empty_representative_detail() {
  return {
    firm_name: '',
    contact_person: '',
    address: '',
    telephone_number: '',
  };
}

function representative_detail_label_map() {
  return new Map([
    ...REPRESENTATIVE_DETAIL_FIELDS.map((field) => [normalize_label(field.label), field.key]),
    ['firm name', 'firm_name'],
    ['firm', 'firm_name'],
    ['law firm', 'firm_name'],
    ['representative name', 'firm_name'],
    ['contact', 'contact_person'],
    ['person', 'contact_person'],
    ['phone', 'telephone_number'],
    ['telephone', 'telephone_number'],
    ['tel', 'telephone_number'],
    ['mobile', 'telephone_number'],
  ]);
}

function normalize_label(value) {
  return normalize_text(value).toLowerCase().replace(/[^a-z0-9]+/g, ' ');
}

function normalize_field_name(value) {
  return normalize_label(value).replace(/ /g, '_');
}

function is_documents_section_name(section_name = '') {
  const normalized_section_name = normalize_label(section_name);
  return normalized_section_name.includes('documents')
    || normalized_section_name.includes('attachments')
    || normalized_section_name.includes('case file');
}

function case_data_config(field_key = '') {
  switch (field_key) {
    case 'known_or_registered_farm':
      return CASE_DATA_FIELD_CONFIG.known_or_registered_farm;
    case 'province':
      return CASE_DATA_FIELD_CONFIG.province;
    case 'town_district':
      return CASE_DATA_FIELD_CONFIG.town_district;
    case 'municipality':
      return CASE_DATA_FIELD_CONFIG.municipality;
    case 'select_from_existing_farms':
      return CASE_DATA_FIELD_CONFIG.select_from_existing_farms;
    default:
      return {};
  }
}

function party_field_config(field_key = '') {
  switch (field_key) {
    case 'applicants':
      return PARTY_FIELD_CONFIG.applicants;
    case 'applicants_legal_representatives':
      return PARTY_FIELD_CONFIG.applicants_legal_representatives;
    case 'respondents':
      return PARTY_FIELD_CONFIG.respondents;
    case 'respondents_legal_representatives':
      return PARTY_FIELD_CONFIG.respondents_legal_representatives;
    default:
      return {};
  }
}

function representative_verification_field_key(field_key = '') {
  switch (field_key) {
    case 'applicants_legal_representatives':
      return REPRESENTATIVE_VERIFICATION_FIELD_BY_REPRESENTATIVE_FIELD.applicants_legal_representatives;
    case 'respondents_legal_representatives':
      return REPRESENTATIVE_VERIFICATION_FIELD_BY_REPRESENTATIVE_FIELD.respondents_legal_representatives;
    default:
      return '';
  }
}

function party_field_default_value(field_key = '') {
  switch (field_key) {
    case 'applicants':
      return PARTY_FIELD_DEFAULT_VALUES.applicants;
    case 'applicants_legal_representatives':
      return [build_empty_representative_detail()];
    case 'applicants_legal_representatives_verified':
      return [...PARTY_FIELD_DEFAULT_VALUES.applicants_legal_representatives_verified];
    case 'respondents':
      return PARTY_FIELD_DEFAULT_VALUES.respondents;
    case 'respondents_legal_representatives':
      return [build_empty_representative_detail()];
    case 'respondents_legal_representatives_verified':
      return [...PARTY_FIELD_DEFAULT_VALUES.respondents_legal_representatives_verified];
    default:
      return '';
  }
}

function read_party_field_value(source = {}, field_key = '') {
  switch (field_key) {
    case 'applicants':
      return source?.applicants;
    case 'applicants_legal_representatives':
      return source?.applicants_legal_representatives;
    case 'applicants_legal_representatives_verified':
      return source?.applicants_legal_representatives_verified;
    case 'respondents':
      return source?.respondents;
    case 'respondents_legal_representatives':
      return source?.respondents_legal_representatives;
    case 'respondents_legal_representatives_verified':
      return source?.respondents_legal_representatives_verified;
    default:
      return '';
  }
}

function write_repeatable_party_field_value(form_values = {}, field_key = '', next_value = []) {
  switch (field_key) {
    case 'applicants_legal_representatives':
      return { ...form_values, applicants_legal_representatives: next_value };
    case 'applicants_legal_representatives_verified':
      return { ...form_values, applicants_legal_representatives_verified: next_value };
    case 'respondents_legal_representatives':
      return { ...form_values, respondents_legal_representatives: next_value };
    case 'respondents_legal_representatives_verified':
      return { ...form_values, respondents_legal_representatives_verified: next_value };
    default:
      return form_values;
  }
}

function parse_saved_case_meta() {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.sessionStorage.getItem(CASE_META_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function persist_case_meta(case_meta_by_application_id) {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(
    CASE_META_STORAGE_KEY,
    JSON.stringify(case_meta_by_application_id || {})
  );
}

function status_variant_from_status(status_label) {
  const normalized_status = String(status_label || '').toLowerCase();

  if (normalized_status.includes('hear')) {
    return 'hearing';
  }

  if (
    normalized_status.includes('stuck')
    || normalized_status.includes('overdue')
    || normalized_status.includes('late')
    || normalized_status.includes('pend')
  ) {
    return 'warning';
  }

  return normalized_status ? 'success' : 'neutral';
}

function build_empty_case_details(application_id) {
  return {
    application_id,
    farm_group: '',
    farm_title: '',
    farm_location: '',
    case_number: '',
    case_citation: '',
    status_label: '',
    status_variant: 'neutral',
    last_filing: '',
    backend_sections: [],
    section_heading_map: {},
    case_data_fields: [],
    case_data: [],
    parties: {
      applicants: '',
      applicants_legal_representatives: '',
      applicants_legal_representatives_verified: '',
      respondents: '',
      respondents_legal_representatives: '',
      respondents_legal_representatives_verified: '',
    },
    pleadings: [],
    interlocutory_matters: [],
    further_actions: [],
    judges_notes: [],
    documents: [],
  };
}

function build_default_case_data_form() {
  return {
    known_or_registered_farm: '',
    province: '',
    town_district: '',
    municipality: '',
    select_from_existing_farms: '',
  };
}

function build_default_parties_form() {
  return {
    applicants: '',
    applicants_legal_representatives: [build_empty_representative_detail()],
    applicants_legal_representatives_verified: [DEFAULT_LEGAL_REPRESENTATIVE_VERIFICATION],
    respondents: '',
    respondents_legal_representatives: [build_empty_representative_detail()],
    respondents_legal_representatives_verified: [DEFAULT_LEGAL_REPRESENTATIVE_VERIFICATION],
  };
}

function merge_multiline_values(existing_value = '', next_value = '') {
  const existing_items = normalize_text(existing_value)
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);
  const next_items = normalize_text(next_value)
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

  return [...existing_items, ...next_items].join('\n');
}

function split_text_blocks(value = '') {
  const normalized = String(value || '').replace(/\r\n/g, '\n').trim();
  if (!normalized) {
    return [];
  }

  return normalized
    .split(/\n\s*\n+/)
    .map((item) => item.replace(/\s*\n\s*/g, ' ').trim())
    .filter(Boolean);
}

function split_representative_text_blocks(value = '') {
  if (Array.isArray(value)) {
    return value;
  }

  if (is_representative_detail_object(value)) {
    return [value];
  }

  const normalized = String(value || '').replace(/\r\n/g, '\n').trim();
  if (!normalized) {
    return [];
  }

  return normalized
    .split(/\n\s*\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalize_text_blocks(values = []) {
  const normalized_items = (Array.isArray(values) ? values : [])
    .map((item) => String(item || '').replace(/\r\n/g, '\n').trim())
    .filter(Boolean);

  return normalized_items.length ? normalized_items : [''];
}

function normalize_legal_representative_verification(value = '') {
  if (is_representative_detail_object(value)) {
    return normalize_representative_verification_value(value);
  }

  const normalized_value = normalize_text(value).toLowerCase();
  if (
    normalized_value === 'yes'
    || normalized_value === 'true'
    || normalized_value === 'verified'
    || normalized_value.includes('details: verified')
    || normalized_value.includes('details verified: verified')
  ) {
    return 'Verified';
  }

  return DEFAULT_LEGAL_REPRESENTATIVE_VERIFICATION;
}

function representative_verification_pattern() {
  return REPRESENTATIVE_VERIFICATION_PATTERN;
}

function representative_verification_match(value = '') {
  return normalize_text(value).match(representative_verification_pattern());
}

function representative_detail_from_block(value = '') {
  return normalize_text(value)
    .replace(representative_verification_pattern(), '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n');
}

function representative_detail_line_break_pattern() {
  return /\s+(Representative name|Telephone number|Contact person|Firm name|Law firm|Telephone|Address|Contact|Mobile|Phone|Name|Firm|Tel)\s*:/gi;
}

function split_representative_detail_lines(value = '') {
  return normalize_text(value)
    .replace(representative_detail_line_break_pattern(), '\n$1:')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function is_representative_detail_object(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function normalize_representative_field_text(value = '') {
  if (!is_representative_detail_object(value)) {
    const normalized_value = normalize_text(value);
    return normalized_value === '[object Object]' ? '' : normalized_value;
  }

  const direct_value = [
    value.value,
    value.answer_text,
    value.text,
    value.label,
    value.name,
    value.display_name,
    value.title,
  ].find((item) => !is_representative_detail_object(item) && normalize_text(item));

  if (direct_value !== undefined) {
    return normalize_text(direct_value);
  }

  const object_values = Object.values(value);
  if (object_values.length === 1) {
    return normalize_representative_field_text(object_values[0]);
  }

  return '';
}

function source_has_key(source = {}, key = '') {
  return Object.prototype.hasOwnProperty.call(source, key);
}

function read_firm_name_value(source = {}) {
  if (source_has_key(source, 'firm_name')) return source.firm_name;
  if (source_has_key(source, 'name')) return source.name;
  if (source_has_key(source, 'firm')) return source.firm;
  if (source_has_key(source, 'law_firm')) return source.law_firm;
  if (source_has_key(source, 'representative_name')) return source.representative_name;
  return '';
}

function read_contact_person_value(source = {}) {
  if (source_has_key(source, 'contact_person')) return source.contact_person;
  if (source_has_key(source, 'contact')) return source.contact;
  if (source_has_key(source, 'person')) return source.person;
  return '';
}

function read_address_value(source = {}) {
  return source_has_key(source, 'address') ? source.address : '';
}

function read_telephone_number_value(source = {}) {
  if (source_has_key(source, 'telephone_number')) return source.telephone_number;
  if (source_has_key(source, 'telephone')) return source.telephone;
  if (source_has_key(source, 'phone')) return source.phone;
  if (source_has_key(source, 'tel')) return source.tel;
  if (source_has_key(source, 'mobile')) return source.mobile;
  return '';
}

function read_representative_detail_value(source = {}, field_key = '') {
  switch (field_key) {
    case 'firm_name':
      return normalize_representative_field_text(read_firm_name_value(source));
    case 'contact_person':
      return normalize_representative_field_text(read_contact_person_value(source));
    case 'address':
      return normalize_representative_field_text(read_address_value(source));
    case 'telephone_number':
      return normalize_representative_field_text(read_telephone_number_value(source));
    default:
      return '';
  }
}

function verification_match(value = '', found = false) {
  return {
    found,
    value,
  };
}

function read_primary_verification_match(source = {}) {
  if (source_has_key(source, 'representative_details_verified')) {
    return verification_match(source.representative_details_verified, true);
  }
  if (source_has_key(source, 'representative_details')) {
    return verification_match(source.representative_details, true);
  }
  if (source_has_key(source, 'details_verified')) {
    return verification_match(source.details_verified, true);
  }
  if (source_has_key(source, 'verified')) {
    return verification_match(source.verified, true);
  }
  return verification_match();
}

function read_secondary_verification_match(source = {}) {
  if (source_has_key(source, 'is_verified')) {
    return verification_match(source.is_verified, true);
  }
  if (source_has_key(source, 'verification')) {
    return verification_match(source.verification, true);
  }
  if (source_has_key(source, 'verification_status')) {
    return verification_match(source.verification_status, true);
  }
  if (source_has_key(source, 'status')) {
    return verification_match(source.status, true);
  }
  return verification_match();
}

function normalize_representative_verification_value(value = '') {
  if (!is_representative_detail_object(value)) {
    return normalize_legal_representative_verification(value);
  }

  const primary_match = read_primary_verification_match(value);
  const secondary_match = primary_match.found
    ? verification_match()
    : read_secondary_verification_match(value);
  const source_match = primary_match.found ? primary_match : secondary_match;

  if (source_match.found) {
    return normalize_legal_representative_verification(source_match.value);
  }

  const nested_value = [
    value.value,
    value.answer_text,
    value.text,
    value.label,
    value.name,
  ].find((item) => normalize_text(item));

  return nested_value !== undefined
    ? normalize_legal_representative_verification(nested_value)
    : DEFAULT_LEGAL_REPRESENTATIVE_VERIFICATION;
}

function assign_representative_detail_value(detail, field_key = '', field_value = '') {
  switch (field_key) {
    case 'firm_name':
      detail.firm_name = normalize_text(field_value);
      break;
    case 'contact_person':
      detail.contact_person = normalize_text(field_value);
      break;
    case 'address':
      detail.address = normalize_text(field_value);
      break;
    case 'telephone_number':
      detail.telephone_number = normalize_text(field_value);
      break;
    default:
      break;
  }
}

function read_representative_detail_field(detail, field_key = '') {
  switch (field_key) {
    case 'firm_name':
      return detail.firm_name;
    case 'contact_person':
      return detail.contact_person;
    case 'address':
      return detail.address;
    case 'telephone_number':
      return detail.telephone_number;
    default:
      return '';
  }
}

function representative_detail_from_object(value = {}) {
  return {
    firm_name: read_representative_detail_value(value, 'firm_name'),
    contact_person: read_representative_detail_value(value, 'contact_person'),
    address: read_representative_detail_value(value, 'address'),
    telephone_number: read_representative_detail_value(value, 'telephone_number'),
  };
}

function apply_representative_detail_line(detail, line = '', label_lookup = new Map()) {
  const normalized_line = normalize_text(line);
  const labelled_match = normalized_line.match(/^([^:]+):\s*(.*)$/);
  if (!labelled_match) {
    return false;
  }

  const field_key = label_lookup.get(normalize_label(labelled_match[1]));
  if (!field_key) {
    return false;
  }

  assign_representative_detail_value(detail, field_key, labelled_match[2]);
  return true;
}

function representative_detail_fallback_parts(unmatched_lines = []) {
  const comma_parts = unmatched_lines.length === 1
    ? unmatched_lines[0].split(/\s*,\s*/).map((item) => item.trim()).filter(Boolean)
    : [];

  return comma_parts.length >= 2 ? comma_parts : unmatched_lines;
}

function assign_representative_detail_fallback(detail, fallback_parts = []) {
  const [
    firm_name = '',
    contact_person = '',
    address = '',
    telephone_number = '',
  ] = fallback_parts;
  detail.firm_name = normalize_text(firm_name);
  detail.contact_person = normalize_text(contact_person);
  detail.address = normalize_text(address);
  detail.telephone_number = normalize_text(telephone_number);

  if (fallback_parts.length > REPRESENTATIVE_DETAIL_FIELDS.length) {
    detail.address = normalize_text([
      detail.address,
      ...fallback_parts.slice(REPRESENTATIVE_DETAIL_FIELDS.length),
    ].filter(Boolean).join(', '));
  }
}

function normalize_representative_detail(value = '') {
  const detail = build_empty_representative_detail();
  if (is_representative_detail_object(value)) {
    return representative_detail_from_object(value);
  }

  const text_value = representative_detail_from_block(value);
  if (!text_value) {
    return detail;
  }

  const label_lookup = representative_detail_label_map();
  const unmatched_lines = [];
  split_representative_detail_lines(text_value).forEach((line) => {
    if (apply_representative_detail_line(detail, line, label_lookup)) {
      return;
    }
    unmatched_lines.push(normalize_text(line));
  });

  if (!Object.values(detail).some(Boolean)) {
    assign_representative_detail_fallback(detail, representative_detail_fallback_parts(unmatched_lines));
  } else if (unmatched_lines.length) {
    detail.address = normalize_text([detail.address, ...unmatched_lines].filter(Boolean).join('\n'));
  }

  return detail;
}

function representative_detail_has_value(value = '') {
  return Object.values(normalize_representative_detail(value)).some((item) => normalize_text(item));
}

function serialize_representative_detail(value = '') {
  const detail = normalize_representative_detail(value);
  return REPRESENTATIVE_DETAIL_FIELDS
    .map((field) => {
      const field_value = normalize_text(read_representative_detail_field(detail, field.key));
      return field_value ? `${field.label}: ${field_value}` : '';
    })
    .filter(Boolean)
    .join('\n');
}

function representative_detail_rows_from_block(value = '') {
  const detail = normalize_representative_detail(value);
  return REPRESENTATIVE_DETAIL_FIELDS
    .map((field) => ({
      key: field.key,
      label: field.label,
      value: normalize_text(read_representative_detail_field(detail, field.key)),
    }))
    .filter((field) => field.value);
}

function representative_verification_from_block(value = '') {
  if (is_representative_detail_object(value)) {
    return normalize_representative_verification_value(value);
  }

  const verification_match = representative_verification_match(value);
  if (!verification_match) {
    return DEFAULT_LEGAL_REPRESENTATIVE_VERIFICATION;
  }

  return normalize_legal_representative_verification(verification_match[1]);
}

function split_verification_blocks(value = '') {
  if (Array.isArray(value)) {
    return value;
  }

  if (is_representative_detail_object(value)) {
    return [value];
  }

  return split_text_blocks(value);
}

function normalize_representative_detail_blocks(values = []) {
  const normalized_items = (Array.isArray(values) ? values : [])
    .map((item) => normalize_representative_detail(item))
    .filter((item) => representative_detail_has_value(item));

  return normalized_items.length ? normalized_items : [build_empty_representative_detail()];
}

function normalize_verification_blocks(values = [], representative_values = []) {
  const representative_count = Math.max(
    normalize_text_blocks(representative_values).length,
    1
  );
  const normalized_values = (Array.isArray(values) ? values : [])
    .map((item) => normalize_legal_representative_verification(item));
  const verification_queue = [...normalized_values];

  return Array.from({ length: representative_count }, () =>
    verification_queue.shift() || DEFAULT_LEGAL_REPRESENTATIVE_VERIFICATION
  );
}

function join_representative_blocks_with_verification(representative_values = [], verification_values = []) {
  const representative_blocks = normalize_representative_detail_blocks(representative_values);
  const verification_blocks = normalize_verification_blocks(verification_values, representative_blocks);
  const verification_queue = [...verification_blocks];

  return join_text_blocks(
    representative_blocks
      .map((representative) => {
        const representative_detail = serialize_representative_detail(representative);
        return representative_detail
          ? `${representative_detail}\n${REPRESENTATIVE_VERIFICATION_LABEL}: ${verification_queue.shift()}`
          : '';
      })
      .filter(Boolean)
  );
}

function join_text_blocks(values = []) {
  return (Array.isArray(values) ? values : [])
    .map((item) => is_representative_detail_object(item) ? serialize_representative_detail(item) : normalize_text(item))
    .filter((item) => normalize_text(item))
    .join('\n\n');
}

function merge_text_blocks(existing_value = '', next_values = []) {
  return join_text_blocks([
    ...split_text_blocks(existing_value),
    ...normalize_text_blocks(next_values),
  ]);
}

function merge_representative_text_blocks(existing_value = '', next_values = []) {
  return join_text_blocks([
    ...split_representative_text_blocks(existing_value),
    ...(Array.isArray(next_values) ? next_values : split_representative_text_blocks(next_values)),
  ]);
}

function build_parties_form(values = {}) {
  const applicant_representative_blocks = split_representative_text_blocks(values?.applicants_legal_representatives);
  const respondent_representative_blocks = split_representative_text_blocks(values?.respondents_legal_representatives);
  const applicants_legal_representatives = normalize_representative_detail_blocks(
    applicant_representative_blocks
  );
  const respondents_legal_representatives = normalize_representative_detail_blocks(
    respondent_representative_blocks
  );

  const applicant_verification_blocks = applicant_representative_blocks
    .map((item) => representative_verification_from_block(item));
  const respondent_verification_blocks = respondent_representative_blocks
    .map((item) => representative_verification_from_block(item));

  if (values?.applicants_legal_representatives_verified) {
    applicant_verification_blocks.splice(
      0,
      applicant_verification_blocks.length,
      ...split_verification_blocks(values.applicants_legal_representatives_verified)
    );
  }

  if (values?.respondents_legal_representatives_verified) {
    respondent_verification_blocks.splice(
      0,
      respondent_verification_blocks.length,
      ...split_verification_blocks(values.respondents_legal_representatives_verified)
    );
  }

  return {
    applicants: normalize_text(values?.applicants),
    applicants_legal_representatives,
    applicants_legal_representatives_verified: normalize_verification_blocks(
      applicant_verification_blocks,
      applicants_legal_representatives
    ),
    respondents: normalize_text(values?.respondents),
    respondents_legal_representatives,
    respondents_legal_representatives_verified: normalize_verification_blocks(
      respondent_verification_blocks,
      respondents_legal_representatives
    ),
  };
}

function serialize_parties_form(form_values = {}) {
  return {
    applicants: normalize_text(form_values?.applicants),
    applicants_legal_representatives: join_representative_blocks_with_verification(
      form_values?.applicants_legal_representatives,
      form_values?.applicants_legal_representatives_verified
    ),
    respondents: normalize_text(form_values?.respondents),
    respondents_legal_representatives: join_representative_blocks_with_verification(
      form_values?.respondents_legal_representatives,
      form_values?.respondents_legal_representatives_verified
    ),
  };
}

function is_party_field_key(field_key = '') {
  return PARTY_FIELD_KEYS.includes(field_key);
}

function is_legal_representative_field_key(field_key = '') {
  return LEGAL_REPRESENTATIVE_FIELD_KEYS.includes(field_key);
}

function is_legal_representative_verification_field_key(field_key = '') {
  return field_key === 'applicants_legal_representatives_verified'
    || field_key === 'respondents_legal_representatives_verified';
}

function is_repeatable_party_field_key(field_key = '') {
  return is_legal_representative_field_key(field_key)
    || is_legal_representative_verification_field_key(field_key);
}

function read_scalar_party_field(source = {}, field_key = '') {
  if (is_party_field_key(field_key) && !is_repeatable_party_field_key(field_key)) {
    return read_party_field_value(source, field_key) || party_field_default_value(field_key);
  }

  return '';
}

function read_repeatable_party_field(source = {}, field_key = '') {
  if (is_repeatable_party_field_key(field_key)) {
    return read_party_field_value(source, field_key) || party_field_default_value(field_key);
  }

  return [''];
}

function read_question_map_entry(question_map = {}, field_key = '') {
  if (!is_party_field_key(field_key)) {
    return null;
  }

  return read_party_field_value(question_map, field_key) || null;
}

function read_serialized_party_value(serialized_form_values = {}, field_key = '') {
  if (!is_party_field_key(field_key)) {
    return '';
  }

  return normalize_text(read_party_field_value(serialized_form_values, field_key));
}

function merge_party_answer_text(field_key, current_parties = {}, serialized_form_values = {}, raw_form_values = {}, append_mode = false) {
  const incoming_value = read_serialized_party_value(serialized_form_values, field_key);
  if (!append_mode) {
    return incoming_value;
  }

  if (is_repeatable_party_field_key(field_key)) {
    if (is_legal_representative_field_key(field_key)) {
      return merge_representative_text_blocks(
        read_repeatable_party_field(current_parties, field_key),
        split_representative_text_blocks(read_party_field_value(serialized_form_values, field_key))
      );
    }

    return merge_text_blocks(
      read_repeatable_party_field(current_parties, field_key),
      read_repeatable_party_field(raw_form_values, field_key)
    );
  }

  return merge_multiline_values(
    read_scalar_party_field(current_parties, field_key),
    incoming_value
  );
}

function build_parties_payload_entries(question_map = {}, current_parties = {}, form_values = {}, options = {}) {
  const serialized_form_values = serialize_parties_form(form_values);

  return PARTY_FIELD_KEYS
    .map((field_key) => {
      const question_entry = read_question_map_entry(question_map, field_key);
      const answer_text = merge_party_answer_text(
        field_key,
        current_parties,
        serialized_form_values,
        form_values,
        Boolean(options?.append_mode)
      );

      if (!question_entry?.application_form_id || !question_entry?.section_question_id || !answer_text) {
        return null;
      }

      return {
        application_form_id: Number(question_entry.application_form_id),
        section_question_id: Number(question_entry.section_question_id),
        section_index: Number(question_entry.section_index || 0),
        answer_text,
      };
    })
    .filter(Boolean);
}

function group_payload_entries_by_application_form(payload_entries = []) {
  const grouped_entries = new Map();
  payload_entries.forEach((entry) => {
    const key = String(entry.application_form_id);
    if (!grouped_entries.has(key)) {
      grouped_entries.set(key, {
        application_form_id: entry.application_form_id,
        current_section_index: entry.section_index,
        answers: [],
      });
    }

    const payload = grouped_entries.get(key);
    payload.current_section_index = Math.max(
      payload.current_section_index,
      entry.section_index
    );
    payload.answers.push({
      section_question_id: entry.section_question_id,
      answer_text: entry.answer_text,
    });
  });

  return Object.fromEntries(grouped_entries);
}

function update_repeatable_party_blocks(form_values = {}, field_key = '', updater = (values) => values) {
  if (!is_repeatable_party_field_key(field_key)) {
    return form_values;
  }

  return write_repeatable_party_field_value(
    form_values,
    field_key,
    updater([...read_repeatable_party_field(form_values, field_key)])
  );
}

function build_default_filing_form() {
  return {
    filing_date: new Date().toISOString().slice(0, 10),
    title: '',
    actor: 'Applicants',
    description: '',
  };
}

function build_default_matter_form() {
  return {
    matter_date: new Date().toISOString().slice(0, 10),
    title: '',
    status: 'Pending',
    description: '',
  };
}

function build_default_note_form() {
  return {
    note_date: new Date().toISOString().slice(0, 10),
    body: '',
  };
}

function build_default_further_action_form() {
  return {
    action_date: new Date().toISOString().slice(0, 10),
    title: '',
    correspondence: '',
    status: 'Pending',
    reminder_at: '',
  };
}

function build_default_hearing_schedule_form() {
  return {
    hearing_date: new Date().toISOString().slice(0, 10),
    notes: '',
  };
}

function build_default_document_form() {
  return {
    document_date: new Date().toISOString().slice(0, 10),
    category: 'Pleading',
    party: 'Applicants',
    file: null,
    files: [],
    file_name: '',
    file_size_label: '',
  };
}

function first_non_empty_value(values = []) {
  return values.find((value) => normalize_text(value)) || '';
}

function normalize_select_value_to_option(value = '', options = []) {
  const normalized_value = normalize_text(value);
  if (!normalized_value) {
    return '';
  }

  const matching_option = (Array.isArray(options) ? options : []).find(
    (option) => normalize_label(option) === normalize_label(normalized_value)
  );

  return matching_option || normalized_value;
}

function case_data_accessor(field_key = '') {
  return CASE_DATA_FIELD_ACCESSORS.find((accessor) => accessor.key === field_key) || null;
}

function read_case_data_field_value(source = {}, field_key = '') {
  const accessor = case_data_accessor(field_key);
  return accessor ? accessor.read_value(source) : '';
}

function read_case_data_question_entry(question_map = {}, field_key = '') {
  const accessor = case_data_accessor(field_key);
  return accessor ? accessor.read_question(question_map) || null : null;
}

function read_case_data_existing_field(existing_fields_by_key = {}, field_key = '') {
  return existing_fields_by_key instanceof Map
    ? existing_fields_by_key.get(field_key) || {}
    : {};
}

function normalize_case_data_field_value(field_key = '', value = '', question_entry = null) {
  const config = case_data_config(field_key);
  const input_type = config.type || case_data_input_type(question_entry);

  if (input_type !== 'select') {
    return normalize_text(value);
  }

  const options = Array.isArray(question_entry?.options) && question_entry.options.length
    ? question_entry.options
    : (config.options || []);

  return normalize_select_value_to_option(value, options);
}

function format_date(value) {
  const normalized = normalize_text(value);
  if (!normalized) {
    return '';
  }

  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return normalized;
  }

  return parsed.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function build_status_label(status_label, hearing_date = '') {
  const normalized_status = normalize_text(status_label);
  if (!normalized_status) {
    return '';
  }

  if (!normalize_label(normalized_status).includes('hearing')) {
    return normalized_status;
  }

  const formatted_hearing_date = format_date(hearing_date);
  return formatted_hearing_date ? `${normalized_status} - ${formatted_hearing_date}` : normalized_status;
}

function format_file_size(bytes) {
  const value = Number(bytes || 0);
  if (!value) {
    return '';
  }

  if (value >= 1024 * 1024) {
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (value >= 1024) {
    return `${Math.round(value / 1024)} KB`;
  }

  return `${value} B`;
}

function extension_from_filename(filename) {
  const normalized = normalize_text(filename);
  const segments = normalized.split('.');
  return segments.length > 1 ? segments[segments.length - 1].toUpperCase() : '';
}

function extension_variant(extension) {
  const normalized = normalize_text(extension).toLowerCase();
  if (normalized === 'pdf') {
    return 'pdf';
  }
  if (normalized === 'docx' || normalized === 'doc') {
    return 'docx';
  }
  return 'file';
}

function flatten_questions(application_forms = []) {
  return application_forms.flatMap((application_form) =>
    (application_form?.form?.sections || []).flatMap((section, section_index) =>
      (section?.questions || []).map((question) => ({
        application_form_id: Number(application_form?.application_form_id || 0),
        form_id: Number(application_form?.form?.id || 0),
        section_index,
        section_name: normalize_text(section?.name),
        section_question_id: Number(question?.section_question_id || 0),
        question_text: normalize_text(question?.question_text),
        question_label: normalize_label(question?.question_text),
        field_name: normalize_field_name(
          question?.data_point?.data_point_name
          || question?.question?.data_point?.data_point_name
          || question?.data_point_name
      ),
      answer: normalize_text(question?.answer),
      presigned_url: normalize_text(question?.presigned_url),
      question_type: normalize_text(question?.question_type?.question_type),
      options: extract_question_options(question),
      order_number: Number(question?.order_number || 0),
    }))
  )
);
}

function extract_question_options(question = {}) {
  const option_sets = [
    question?.options,
    question?.question_options,
    question?.possible_answers,
    question?.choices,
    question?.multi_select_choices,
    question?.question?.options,
    question?.question?.question_options,
    question?.question?.possible_answers,
    question?.question?.choices,
    question?.question?.multi_select_choices,
  ];
  const first_option_set = option_sets.find(Array.isArray);

  return Array.from(
    new Set(
      (first_option_set || [])
        .map((option) =>
          normalize_text(
            option?.option_text
            || option?.option
            || option?.label
            || option?.name
            || option?.value
            || option?.choice
            || option
          )
        )
        .filter(Boolean)
    )
  );
}

function normalize_question_match_config(config = {}) {
  if (Array.isArray(config)) {
    return {
      labels: config,
      field_names: [],
    };
  }

  return {
    labels: Array.isArray(config?.labels) ? config.labels : [],
    field_names: Array.isArray(config?.field_names) ? config.field_names : [],
  };
}

function humanize_field_key(field_key = '') {
  const normalized = normalize_field_name(field_key).replace(/_/g, ' ').trim();
  if (!normalized) {
    return '';
  }

  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function singularize_party_field_name(field_name = '') {
  return normalize_field_name(field_name)
    .replace(/^applicants(?=_|$)/, 'applicant')
    .replace(/^respondents(?=_|$)/, 'respondent')
    .replace(/representatives(?=_|$)/g, 'representative');
}

function build_case_data_field_match_config(field_key = '') {
  const config = case_data_config(field_key);

  return {
    field_names: [normalize_field_name(field_key)],
    labels: config.labels || [],
  };
}

function build_case_citation_match_config() {
  return {
    field_names: ['case_citation'],
    labels: ['case citation', 'citation', 'case name', 'matter'],
  };
}

function build_party_field_match_config(field_key = '') {
  const normalized_field_key = normalize_field_name(field_key);
  const singular_field_key = singularize_party_field_name(normalized_field_key);
  const config = party_field_config(field_key);
  const field_names = Array.from(
    new Set([normalized_field_key, singular_field_key].filter(Boolean))
  );

  return {
    field_names,
    labels: [
      ...field_names.map((value) => value.replace(/_/g, ' ')),
      ...(config.labels || []),
    ],
  };
}

function question_entry_matches_config(entry = {}, config = {}) {
  const match_config = normalize_question_match_config(config);
  const entry_field_name = normalize_field_name(entry?.field_name);
  const normalized_field_names = match_config.field_names.map(normalize_field_name);

  if (entry_field_name && normalized_field_names.includes(entry_field_name)) {
    return true;
  }

  const normalized_labels = match_config.labels.map(normalize_label);
  return normalized_labels.some((candidate) => entry.question_label.includes(candidate));
}

function profile_answer_field_name(answer = {}) {
  return first_non_empty_value([
    answer?.data_point,
    answer?.data_point_name,
    answer?.field_name,
  ]);
}

function profile_application_form_id(answer = {}) {
  return Number(first_non_empty_value([
    answer?.application_form_id,
    answer?.form_submission_id,
    0,
  ]));
}

function build_profile_question_entry(answer = {}) {
  const section_question_id = Number(answer?.section_question_id || 0);
  if (!section_question_id) {
    return null;
  }

  return {
    application_form_id: profile_application_form_id(answer),
    form_id: Number(answer?.form_id || 0),
    section_index: Number(answer?.section_index || 999),
    section_name: normalize_text(answer?.section_name),
    section_question_id,
    question_id: Number(answer?.question_id || 0),
    question_text: normalize_text(answer?.question_text),
    question_label: normalize_label(answer?.question_text),
    field_name: normalize_field_name(profile_answer_field_name(answer)),
    answer: normalize_text(answer?.answer),
    presigned_url: normalize_text(answer?.presigned_url),
    question_type: normalize_text(answer?.question_type),
    options: [],
    order_number: Number(answer?.order_number || 999),
    source: 'profile_answer',
  };
}

function build_profile_answer_map(form_answers = []) {
  return new Map(
    (Array.isArray(form_answers) ? form_answers : [])
      .map(build_profile_question_entry)
      .filter((entry) => entry?.section_question_id && normalize_text(entry.answer))
      .map((entry) => [entry.section_question_id, entry])
  );
}

function merge_profile_answers_into_question_entries(question_entries = [], form_answers = []) {
  const profile_entries_by_section_question_id = build_profile_answer_map(form_answers);
  if (!profile_entries_by_section_question_id.size) {
    return Array.isArray(question_entries) ? question_entries : [];
  }

  const merged_entries = (Array.isArray(question_entries) ? question_entries : []).map((entry) => {
    const profile_entry = profile_entries_by_section_question_id.get(Number(entry?.section_question_id || 0));
    if (!profile_entry) {
      return entry;
    }

    return {
      ...entry,
      application_form_id: entry.application_form_id || profile_entry.application_form_id,
      answer: profile_entry.answer || entry.answer,
      source: entry.source || 'application_form',
    };
  });
  const existing_section_question_ids = new Set(
    merged_entries.map((entry) => Number(entry?.section_question_id || 0)).filter(Boolean)
  );
  const missing_profile_entries = Array.from(profile_entries_by_section_question_id.values())
    .filter((entry) => !existing_section_question_ids.has(Number(entry?.section_question_id || 0)));

  return [...merged_entries, ...missing_profile_entries];
}

function read_backend_question_options(question = {}) {
  return Array.from(
    new Set(
      [
        ...(Array.isArray(question?.multiselect_set) ? question.multiselect_set : []),
        ...(Array.isArray(question?.options) ? question.options : []),
      ]
        .map((option) => normalize_text(option?.choice || option?.label || option?.value || option))
        .filter(Boolean)
    )
  );
}

function build_backend_section_question_entry({
  application_form_id = 0,
  form_id = 0,
  main_section = {},
  sub_section = {},
  section_question = {},
  answer_entry = null,
}) {
  const question = section_question?.question || {};

  return {
    application_form_id,
    form_id,
    main_section_id: Number(main_section?.id || 0),
    main_section_name: normalize_text(main_section?.name),
    section_id: Number(sub_section?.section?.id || 0),
    section_name: normalize_text(sub_section?.section?.name),
    section_question_id: Number(section_question?.id || 0),
    question_id: Number(question?.id || 0),
    question_text: normalize_text(question?.question_text),
    question_type: normalize_text(question?.question_type?.question_type),
    options: read_backend_question_options(question),
    answer: normalize_text(answer_entry?.answer),
    presigned_url: normalize_text(answer_entry?.presigned_url),
    is_mandatory: Boolean(section_question?.is_mandatory),
    order_number: Number(section_question?.order_number || 0),
  };
}

function normalize_backend_case_sections(complete_forms = [], question_entries = []) {
  const answers_by_section_question_id = new Map(
    (Array.isArray(question_entries) ? question_entries : [])
      .filter((entry) => Number(entry?.section_question_id || 0))
      .map((entry) => [Number(entry.section_question_id), entry])
  );

  return (Array.isArray(complete_forms) ? complete_forms : []).flatMap((form) =>
    (form?.form_main_sections || []).map((main_section_link) => {
      const main_section = main_section_link?.main_section || {};
      const sub_sections = (main_section_link?.form_main_sections || [])
        .map((sub_section) => {
          const questions = (sub_section?.sectionquestion_set || [])
            .map((section_question) => build_backend_section_question_entry({
              application_form_id: Number(form?.application_form_id || 0),
              form_id: Number(form?.id || 0),
              main_section,
              sub_section,
              section_question,
              answer_entry: answers_by_section_question_id.get(Number(section_question?.id || 0)),
            }))
            .filter((question) => question.section_question_id && question.question_text);

          return {
            id: Number(sub_section?.id || 0),
            name: normalize_text(sub_section?.section?.name),
            order_number: Number(sub_section?.order_number || 0),
            questions,
          };
        })
        .filter((section) => section.name || section.questions.length);

      return {
        id: Number(main_section_link?.id || 0),
        name: normalize_text(main_section?.name),
        description: normalize_text(main_section?.description),
        order_number: Number(main_section_link?.order_number || 0),
        sub_sections,
      };
    })
  ).filter((section) => section.name || section.sub_sections.length);
}

function normalize_application_form_case_sections(application_forms = []) {
  return (Array.isArray(application_forms) ? application_forms : []).flatMap((application_form) => {
    const form = application_form?.form || {};
    const sections = Array.isArray(form?.sections) ? form.sections : [];

    if (!sections.length) {
      return [];
    }

    return [{
      id: Number(form?.id || application_form?.form_id || 0),
      name: normalize_text(form?.form_name || form?.name),
      description: normalize_text(form?.form_description || form?.description),
      order_number: 0,
      sub_sections: sections.map((section, section_index) => ({
        id: Number(section?.id || section?.section_id || section_index + 1),
        name: normalize_text(section?.name || section?.section_name),
        order_number: Number(section?.order_number || section_index + 1),
        questions: (Array.isArray(section?.questions) ? section.questions : [])
          .map((question, question_index) => ({
            application_form_id: Number(application_form?.application_form_id || 0),
            form_id: Number(form?.id || application_form?.form_id || 0),
            section_id: Number(section?.id || section?.section_id || 0),
            section_name: normalize_text(section?.name || section?.section_name),
            section_question_id: Number(question?.section_question_id || question?.id || 0),
            question_id: Number(question?.question_id || question?.question?.id || 0),
            question_text: normalize_text(question?.question_text || question?.question?.question_text),
            question_type: normalize_text(question?.question_type?.question_type || question?.question_type),
            options: extract_question_options(question),
            answer: normalize_text(question?.answer),
            presigned_url: normalize_text(question?.presigned_url),
            is_mandatory: Boolean(question?.is_mandatory),
            order_number: Number(question?.order_number || question_index + 1),
          }))
          .filter((question) => question.question_text),
      })).filter((section) => section.name),
    }];
  });
}

function backend_section_name_candidates(backend_sections = []) {
  return (Array.isArray(backend_sections) ? backend_sections : []).flatMap((main_section) => [
    normalize_text(main_section?.name),
    ...(Array.isArray(main_section?.sub_sections) ? main_section.sub_sections : [])
      .map((sub_section) => normalize_text(sub_section?.name)),
  ]).filter(Boolean);
}

function find_backend_section_name_by_aliases(backend_sections = [], aliases = []) {
  const normalized_aliases = aliases.map(normalize_label).filter(Boolean);
  return backend_section_name_candidates(backend_sections).find((section_name) => {
    const normalized_section_name = normalize_label(section_name);
    return normalized_aliases.some((alias) =>
      normalized_section_name.includes(alias) || alias.includes(normalized_section_name)
    );
  }) || '';
}

function rendered_backend_section_key(application_form_id = 0, section_id = 0, section_index = 0, prefix = 'section') {
  return [
    application_form_id || 'form',
    prefix,
    section_id || section_index,
    section_index,
  ].map((value) => String(value ?? '')).join(':');
}

function normalize_backend_render_question(question = {}) {
  return {
    ...question,
    application_form_id: Number(question?.application_form_id || 0),
    form_id: Number(question?.form_id || 0),
    section_id: Number(question?.section_id || 0),
    section_name: normalize_text(question?.section_name),
    section_question_id: Number(question?.section_question_id || 0),
    question_text: normalize_text(question?.question_text),
    question_type: question?.question_type,
    options: Array.isArray(question?.options) && question.options.length
      ? question.options
      : extract_question_options(question),
    answer: normalize_text(question?.answer),
    presigned_url: normalize_text(question?.presigned_url),
    order_number: Number(question?.order_number || 0),
  };
}

function flatten_backend_case_sections_for_render(case_details = {}) {
  return (Array.isArray(case_details?.backend_sections) ? case_details.backend_sections : [])
    .flatMap((main_section, main_index) =>
      (Array.isArray(main_section?.sub_sections) ? main_section.sub_sections : []).map((sub_section, sub_index) => {
        const application_form_id = Number(first_non_empty_value([
          sub_section?.questions?.[0]?.application_form_id,
          main_section?.application_form_id,
          0,
        ]));
        const section_id = Number(sub_section?.id || sub_section?.section_id || 0);

        return {
          ...sub_section,
          application_form_id,
          form_id: Number(first_non_empty_value([sub_section?.questions?.[0]?.form_id, main_section?.id, 0])),
          backend_key: rendered_backend_section_key(
            application_form_id,
            section_id,
            Number(sub_section?.order_number || sub_index + 1),
            'normalized'
          ),
          name: normalize_text(sub_section?.name || main_section?.name),
          order_number: Number(sub_section?.order_number || sub_index + 1 || main_index + 1),
          questions: (Array.isArray(sub_section?.questions) ? sub_section.questions : [])
            .map((question) => normalize_backend_render_question(question)),
        };
      })
    )
    .filter((section) => section.name);
}

function find_section_name_from_question_map(question_map = {}) {
  return Object.values(question_map || {}).find((entry) =>
    normalize_text(entry?.main_section_name || entry?.section_name)
  ) || null;
}

function build_section_heading_map({
  backend_sections = [],
  case_data_question_map = {},
  parties_question_map = {},
}) {
  const case_data_section = find_section_name_from_question_map(case_data_question_map);
  const parties_section = find_section_name_from_question_map(parties_question_map);

  return {
    case_data: normalize_text(
      case_data_section?.main_section_name
      || case_data_section?.section_name
      || find_backend_section_name_by_aliases(backend_sections, ['case data', 'case details', 'matter details'])
    ),
    parties: normalize_text(
      parties_section?.main_section_name
      || parties_section?.section_name
      || find_backend_section_name_by_aliases(backend_sections, ['parties', 'representation', 'applicants', 'respondents'])
    ),
    pleadings: find_backend_section_name_by_aliases(backend_sections, ['pleadings', 'notices', 'filings']),
    interlocutory_matters: find_backend_section_name_by_aliases(backend_sections, ['interlocutory', 'matters']),
    further_actions: find_backend_section_name_by_aliases(backend_sections, ['further actions', 'correspondence', 'actions']),
    judges_notes: find_backend_section_name_by_aliases(backend_sections, ['judge', 'notes']),
    documents: find_backend_section_name_by_aliases(backend_sections, ['documents', 'attachments', 'case file']),
  };
}

function find_question_entry(question_entries = [], config = {}) {
  const matching_entries = question_entries.filter((entry) =>
    question_entry_matches_config(entry, config)
  );

  return matching_entries.find((entry) => normalize_text(entry?.answer))
    || matching_entries[0]
    || null;
}

function case_data_input_type(question_entry = {}) {
  const configured_type = normalize_text(case_data_config(question_entry?.field_key).type);
  if (configured_type) {
    return configured_type;
  }

  const question_type = normalize_label(question_entry?.question_type);

  if (is_select_question_type(question_entry, question_type)) {
    return 'select';
  }

  if (is_textarea_question_type(question_type)) {
    return 'textarea';
  }

  return 'text';
}

function is_select_question_type(question_entry = {}, question_type = '') {
  return Boolean(question_entry?.options?.length)
    || question_type.includes('select')
    || question_type.includes('dropdown')
    || question_type.includes('radio');
}

function is_textarea_question_type(question_type = '') {
  return question_type.includes('textarea')
    || question_type.includes('long')
    || question_type.includes('paragraph');
}

function build_case_data_question_map(question_entries = []) {
  return Object.fromEntries(
    CASE_DATA_FIELD_KEYS.map((field_key) => [
      field_key,
      find_question_entry(question_entries, build_case_data_field_match_config(field_key)),
    ])
  );
}

function build_party_question_map(question_entries = []) {
  return Object.fromEntries(
    PARTY_FIELD_KEYS.map((field_key) => [
      field_key,
      find_question_entry(question_entries, build_party_field_match_config(field_key)),
    ])
  );
}

function build_case_data_fields(question_map = {}, values = {}) {
  return CASE_DATA_FIELD_KEYS
    .map((field_key) => build_case_data_field(field_key, question_map, values))
    .filter(Boolean);
}

function case_data_field_label(field_key = '', question_entry = null, config = {}) {
  return first_non_empty_value([
    question_entry?.question_text,
    config.label,
    humanize_field_key(field_key),
  ]);
}

function case_data_field_options(question_entry = null, config = {}) {
  if (Array.isArray(question_entry?.options) && question_entry.options.length) {
    return question_entry.options;
  }

  return config.options || [];
}

function case_data_field_tone(field_key = '', value = '') {
  return field_key === 'notice_of_bar' && value ? 'warning-text' : '';
}

function has_editable_case_data_question(question_map = {}, field_key = '') {
  const question_entry = read_case_data_question_entry(question_map, field_key);
  return Boolean(question_entry?.application_form_id && question_entry?.section_question_id);
}

function build_editable_case_data_field(field_key = '', form_values = {}, existing_fields_by_key = new Map()) {
  const config = case_data_config(field_key);
  const existing_field = read_case_data_existing_field(existing_fields_by_key, field_key);

  return {
    key: field_key,
    label: first_non_empty_value([
      existing_field.label,
      config.label,
      humanize_field_key(field_key),
    ]),
    value: normalize_text(read_case_data_field_value(form_values, field_key) || existing_field.value),
    input_type: existing_field.input_type || config.type || 'text',
    options: existing_field.options?.length ? existing_field.options : (config.options || []),
    tone: existing_field.tone || '',
  };
}

function build_case_data_field(field_key = '', question_map = {}, values = {}) {
  const question_entry = read_case_data_question_entry(question_map, field_key);
  const config = case_data_config(field_key);
  const value = normalize_case_data_field_value(
    field_key,
    read_case_data_field_value(values, field_key),
    question_entry
  );

  if (!question_entry && !value) {
    return null;
  }

  return {
    key: field_key,
    label: case_data_field_label(field_key, question_entry, config),
    value,
    input_type: config.type || case_data_input_type(question_entry),
    options: case_data_field_options(question_entry, config),
    tone: case_data_field_tone(field_key, value),
  };
}

function build_case_data_items(case_data_fields = []) {
  return (Array.isArray(case_data_fields) ? case_data_fields : [])
    .filter((item) => normalize_text(item?.value));
}

function document_meta_storage_key(source, file_name) {
  return `${normalize_text(source).toLowerCase()}::${normalize_text(file_name).toLowerCase()}`;
}

function document_hidden_storage_key(document = {}) {
  return [
    normalize_text(document?.source).toLowerCase(),
    String(document?.id || ''),
    normalize_text(document?.name || document?.file_name).toLowerCase(),
  ].join('::');
}

function inferred_document_party(raw_document = {}) {
  const section = normalize_text(raw_document?.section).toLowerCase();
  const question = normalize_text(raw_document?.question).toLowerCase();
  const combined = `${section} ${question}`;

  if (combined.includes('applicant')) {
    return 'Applicants';
  }
  if (combined.includes('respond')) {
    return 'Respondent';
  }
  if (combined.includes('court')) {
    return 'Court';
  }

  return '';
}

function map_documents(raw_documents = [], saved_meta = {}) {
  const hidden_document_keys = new Set(saved_meta?.hidden_document_keys || []);

  return raw_documents
    .map((item) => {
      const file_name = normalize_text(item?.file_name) || normalize_text(item?.file_url);
      const extension = extension_from_filename(file_name);
      const source = normalize_text(item?.source) || 'document';
      const display_meta = saved_meta?.documents_display_meta?.[document_meta_storage_key(source, file_name)] || {};
      const party = display_meta.party || inferred_document_party(item) || 'Document';
      const category = display_meta.category || normalize_text(item?.question) || normalize_text(item?.source) || 'Document';

      const mapped_document = {
        id: Number(item?.id || 0) || `${file_name}-${item?.submitted_on || ''}`,
        source,
        name: file_name,
        party,
        party_variant: actor_variant_from_actor(party),
        category,
        size: display_meta.size || '',
        type: category,
        date: format_date(display_meta.date || item?.submitted_on),
        extension,
        extension_variant: extension_variant(extension),
        document_url: first_non_empty_value([
          item?.presigned_url,
          item?.preview_url,
          item?.url,
          item?.document_url,
          item?.file_url,
        ]),
        presigned_url: normalize_text(item?.presigned_url),
      };

      return {
        ...mapped_document,
        hidden_storage_key: document_hidden_storage_key(mapped_document),
      };
    })
    .filter((item) => !hidden_document_keys.has(item.hidden_storage_key));
}

function persist_document_display_meta(application_id, document_meta = {}) {
  const case_meta_by_application_id = parse_saved_case_meta();
  const application_key = String(application_id);
  const existing_case_meta = case_meta_by_application_id[application_key] || {};
  const file_name = normalize_text(document_meta?.file_name);
  const source = normalize_text(document_meta?.source || 'additional');

  if (!file_name) {
    return;
  }

  case_meta_by_application_id[application_key] = {
    ...existing_case_meta,
    documents_display_meta: {
      ...(existing_case_meta.documents_display_meta || {}),
      [document_meta_storage_key(source, file_name)]: {
        category: normalize_text(document_meta?.category),
        party: normalize_text(document_meta?.party),
        date: normalize_text(document_meta?.date),
        size: normalize_text(document_meta?.size),
      },
    },
  };

  persist_case_meta(case_meta_by_application_id);
}

function persist_hidden_document_key(application_id, hidden_key) {
  const normalized_key = normalize_text(hidden_key);
  if (!normalized_key) {
    return;
  }

  const case_meta_by_application_id = parse_saved_case_meta();
  const application_key = String(application_id);
  const existing_case_meta = case_meta_by_application_id[application_key] || {};
  const hidden_document_keys = new Set(existing_case_meta.hidden_document_keys || []);
  hidden_document_keys.add(normalized_key);

  case_meta_by_application_id[application_key] = {
    ...existing_case_meta,
    hidden_document_keys: Array.from(hidden_document_keys),
  };

  persist_case_meta(case_meta_by_application_id);
}

function serialize_filing_payload(filing_form = {}) {
  return `${FILING_MARKER}${JSON.stringify({
    date: normalize_text(filing_form.filing_date),
    title: normalize_text(filing_form.title),
    actor: normalize_text(filing_form.actor),
    description: normalize_text(filing_form.description),
  })}`;
}

function serialize_matter_payload(matter_form = {}) {
  return `${MATTER_MARKER}${JSON.stringify({
    date: normalize_text(matter_form.matter_date),
    title: normalize_text(matter_form.title),
    status: normalize_text(matter_form.status),
    description: normalize_text(matter_form.description),
  })}`;
}

function serialize_judge_note_payload(note_form = {}) {
  return `${JUDGE_NOTE_MARKER}${JSON.stringify({
    date: normalize_text(note_form.note_date),
    body: normalize_text(note_form.body),
  })}`;
}

function serialize_further_action_payload(action_form = {}) {
  const status = normalize_further_action_status(action_form.status);
  return `${FURTHER_ACTION_MARKER}${JSON.stringify({
    title: normalize_text(action_form.title),
    correspondence: normalize_text(action_form.correspondence),
    status,
    reminder_at: reminder_value_for_further_action_status(status, action_form.reminder_at),
  })}`;
}

function split_serialized_payload_stream(raw_value = '') {
  return String(raw_value || '')
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean);
}

function parse_serialized_payload_stream(raw_value = '', parse_payload = (block) => block) {
  return split_serialized_payload_stream(raw_value)
    .map((block, index) => ({
      index,
      payload: parse_payload(block),
    }))
    .filter((entry) => Boolean(entry.payload));
}

function join_serialized_payload_stream(payload_blocks = []) {
  return (Array.isArray(payload_blocks) ? payload_blocks : [])
    .map((block) => normalize_text(block))
    .filter(Boolean)
    .join('\n\n');
}

const JOURNAL_FORM_CONFIG = Object.freeze({
  filing: {
    form_key: 'filing_form',
    saving_key: 'saving_filing',
    showing_add_key: 'showing_add_filing',
    show_edit_modal_key: 'show_edit_filing_modal',
    editing_id_key: 'editing_filing_id',
    date_key: 'filing_date',
    required_key: 'title',
    context_message: 'Missing application or user context for saving a filing.',
    required_message: 'A filing title is required.',
    failure_message: 'Failed to save the filing.',
    build_default_form: build_default_filing_form,
    serialize_payload: serialize_filing_payload,
    build_form_from_item(item = {}) {
      return {
        filing_date: normalize_text(item?.date_raw || item?.journal_date || '').slice(0, 10) || new Date().toISOString().slice(0, 10),
        title: normalize_text(item?.title),
        actor: normalize_text(item?.actor) || 'Other',
        description: normalize_text(item?.subtitle),
      };
    },
  },
  matter: {
    form_key: 'matter_form',
    saving_key: 'saving_matter',
    showing_add_key: 'showing_add_matter',
    show_edit_modal_key: 'show_edit_matter_modal',
    editing_id_key: 'editing_matter_id',
    date_key: 'matter_date',
    required_key: 'title',
    context_message: 'Missing application or user context for saving a matter.',
    required_message: 'An interlocutory matter title is required.',
    failure_message: 'Failed to save the interlocutory matter.',
    build_default_form: build_default_matter_form,
    serialize_payload: serialize_matter_payload,
    build_form_from_item(item = {}) {
      return {
        matter_date: normalize_text(item?.date_raw || item?.journal_date || '').slice(0, 10) || new Date().toISOString().slice(0, 10),
        title: normalize_text(item?.title),
        status: normalize_matter_status(item?.status),
        description: normalize_text(item?.subtitle),
      };
    },
  },
  note: {
    form_key: 'note_form',
    saving_key: 'saving_note',
    showing_add_key: 'showing_add_note',
    show_edit_modal_key: 'show_edit_note_modal',
    editing_id_key: 'editing_note_id',
    date_key: 'note_date',
    required_key: 'body',
    context_message: 'Missing application or user context for saving a judge note.',
    required_message: 'A judge note is required.',
    failure_message: 'Failed to save the judge note.',
    build_default_form: build_default_note_form,
    serialize_payload: serialize_judge_note_payload,
    build_form_from_item(item = {}) {
      return {
        note_date: normalize_text(item?.date_raw || item?.journal_date || '').slice(0, 10) || new Date().toISOString().slice(0, 10),
        body: normalize_text(item?.body),
      };
    },
  },
  further_action: {
    form_key: 'further_action_form',
    saving_key: 'saving_further_action',
    showing_add_key: 'showing_add_further_action',
    show_edit_modal_key: 'show_edit_further_action_modal',
    editing_id_key: 'editing_further_action_id',
    date_key: 'action_date',
    required_key: 'title',
    context_message: 'Missing application or user context for saving a further action.',
    required_message: 'A further action title is required.',
    failure_message: 'Failed to save the further action.',
    build_default_form: build_default_further_action_form,
    serialize_payload: serialize_further_action_payload,
    build_form_from_item(item = {}) {
      return {
        action_date: normalize_text(item?.date_raw || item?.journal_date || '').slice(0, 10) || new Date().toISOString().slice(0, 10),
        title: normalize_text(item?.title),
        correspondence: normalize_text(item?.correspondence),
        status: normalize_further_action_status(item?.status),
        reminder_at: normalize_text(item?.reminder_at),
      };
    },
  },
});

function journal_form_config(journal_type = '') {
  switch (journal_type) {
    case 'filing':
      return JOURNAL_FORM_CONFIG.filing;
    case 'matter':
      return JOURNAL_FORM_CONFIG.matter;
    case 'note':
      return JOURNAL_FORM_CONFIG.note;
    case 'further_action':
      return JOURNAL_FORM_CONFIG.further_action;
    default:
      return null;
  }
}

function journal_form(vm, journal_type = '') {
  switch (journal_type) {
    case 'filing':
      return vm.filing_form || {};
    case 'matter':
      return vm.matter_form || {};
    case 'note':
      return vm.note_form || {};
    case 'further_action':
      return vm.further_action_form || {};
    default:
      return {};
  }
}

function set_journal_form(vm, journal_type = '', form_values = {}) {
  switch (journal_type) {
    case 'filing':
      vm.filing_form = form_values;
      break;
    case 'matter':
      vm.matter_form = form_values;
      break;
    case 'note':
      vm.note_form = form_values;
      break;
    case 'further_action':
      vm.further_action_form = form_values;
      break;
    default:
      break;
  }
}

function set_journal_add_visible(vm, journal_type = '', is_visible = false) {
  switch (journal_type) {
    case 'filing':
      vm.showing_add_filing = is_visible;
      break;
    case 'matter':
      vm.showing_add_matter = is_visible;
      break;
    case 'note':
      vm.showing_add_note = is_visible;
      break;
    case 'further_action':
      vm.showing_add_further_action = is_visible;
      break;
    default:
      break;
  }
}

function set_journal_edit_visible(vm, journal_type = '', is_visible = false) {
  switch (journal_type) {
    case 'filing':
      vm.show_edit_filing_modal = is_visible;
      break;
    case 'matter':
      vm.show_edit_matter_modal = is_visible;
      break;
    case 'note':
      vm.show_edit_note_modal = is_visible;
      break;
    case 'further_action':
      vm.show_edit_further_action_modal = is_visible;
      break;
    default:
      break;
  }
}

function journal_editing_id(vm, journal_type = '') {
  switch (journal_type) {
    case 'filing':
      return vm.editing_filing_id;
    case 'matter':
      return vm.editing_matter_id;
    case 'note':
      return vm.editing_note_id;
    case 'further_action':
      return vm.editing_further_action_id;
    default:
      return null;
  }
}

function set_journal_editing_id(vm, journal_type = '', editing_id = null) {
  switch (journal_type) {
    case 'filing':
      vm.editing_filing_id = editing_id;
      break;
    case 'matter':
      vm.editing_matter_id = editing_id;
      break;
    case 'note':
      vm.editing_note_id = editing_id;
      break;
    case 'further_action':
      vm.editing_further_action_id = editing_id;
      break;
    default:
      break;
  }
}

function journal_is_saving(vm, journal_type = '') {
  switch (journal_type) {
    case 'filing':
      return vm.saving_filing;
    case 'matter':
      return vm.saving_matter;
    case 'note':
      return vm.saving_note;
    case 'further_action':
      return vm.saving_further_action;
    default:
      return false;
  }
}

function set_journal_saving(vm, journal_type = '', is_saving = false) {
  switch (journal_type) {
    case 'filing':
      vm.saving_filing = is_saving;
      break;
    case 'matter':
      vm.saving_matter = is_saving;
      break;
    case 'note':
      vm.saving_note = is_saving;
      break;
    case 'further_action':
      vm.saving_further_action = is_saving;
      break;
    default:
      break;
  }
}

function journal_required_value(journal_type = '', form_values = {}) {
  switch (journal_type) {
    case 'filing':
    case 'matter':
      return form_values.title;
    case 'note':
      return form_values.body;
    case 'further_action':
      return form_values.title;
    default:
      return '';
  }
}

function journal_date_value(journal_type = '', form_values = {}) {
  switch (journal_type) {
    case 'filing':
      return form_values.filing_date;
    case 'matter':
      return form_values.matter_date;
    case 'note':
      return form_values.note_date;
    case 'further_action':
      return form_values.action_date;
    default:
      return '';
  }
}

function current_user_id() {
  return Number(
    sessionStorage.getItem('current_user_id')
    || sessionStorage.getItem('user_id')
    || 0
  );
}

function journal_validation_message(config = {}, journal_type = '', form_values = {}, application_id = 0, user_id = 0) {
  if (!application_id || !user_id) {
    return config.context_message;
  }

  if (!normalize_text(journal_required_value(journal_type, form_values))) {
    return config.required_message;
  }

  return '';
}

async function persist_journal_entry(config = {}, journal_type = '', form_values = {}, context = {}) {
  const journal_payload = {
    journal_date: journal_date_value(journal_type, form_values) || undefined,
    journal_description: config.serialize_payload(form_values),
  };

  if (context.editing_id) {
    await update_land_court_journal({
      journal_id: context.editing_id,
      ...journal_payload,
    });
    return;
  }

  await create_land_court_journal({
    application_id: context.application_id,
    user_id: context.user_id,
    ...journal_payload,
  });
}

function reset_journal_editor(vm, journal_type = '', config = {}) {
  set_journal_add_visible(vm, journal_type, false);
  set_journal_edit_visible(vm, journal_type, false);
  set_journal_editing_id(vm, journal_type, null);
  set_journal_form(vm, journal_type, config.build_default_form());
}

function should_update_matter_status(item = {}, normalized_next_status = '') {
  return Boolean(Number(item?.id || 0))
    && Boolean(normalized_next_status)
    && normalized_next_status !== item?.status;
}

function matter_status_update_payload(item = {}, normalized_next_status = '') {
  return {
    journal_id: Number(item?.id || 0),
    journal_date: normalize_text(item?.date_raw) || undefined,
    journal_description: serialize_matter_payload({
      title: normalize_text(item?.title),
      status: normalized_next_status,
      description: normalize_text(item?.subtitle),
    }),
  };
}

function should_update_further_action_status(item = {}, normalized_next_status = '') {
  return Boolean(Number(item?.id || 0))
    && Boolean(normalized_next_status)
    && normalized_next_status !== item?.status;
}

function further_action_status_update_payload(item = {}, normalized_next_status = '') {
  return {
    journal_id: Number(item?.id || 0),
    journal_date: normalize_text(item?.date_raw) || undefined,
    journal_description: serialize_further_action_payload({
      title: normalize_text(item?.title),
      correspondence: normalize_text(item?.correspondence),
      status: normalized_next_status,
      reminder_at: reminder_value_for_further_action_status(normalized_next_status, item?.reminder_at),
    }),
  };
}

function parse_filing_payload(journal = {}) {
  const raw_description = normalize_text(journal?.journal_description);
  if (raw_description.startsWith(FILING_MARKER)) {
    try {
      const parsed = JSON.parse(raw_description.slice(FILING_MARKER.length));
      return {
        date: normalize_text(parsed?.date),
        title: normalize_text(parsed?.title),
        actor: normalize_text(parsed?.actor),
        description: normalize_text(parsed?.description),
      };
    } catch {
      return null;
    }
  }

  return null;
}

function parse_matter_payload(journal = {}) {
  const raw_description = normalize_text(journal?.journal_description);
  if (raw_description.startsWith(MATTER_MARKER)) {
    try {
      const parsed = JSON.parse(raw_description.slice(MATTER_MARKER.length));
      return {
        date: normalize_text(parsed?.date),
        title: normalize_text(parsed?.title),
        status: normalize_text(parsed?.status),
        description: normalize_text(parsed?.description),
      };
    } catch {
      return null;
    }
  }

  return null;
}

function parse_judge_note_payload(journal = {}) {
  const raw_description = normalize_text(journal?.journal_description);
  if (raw_description.startsWith(JUDGE_NOTE_MARKER)) {
    try {
      const parsed = JSON.parse(raw_description.slice(JUDGE_NOTE_MARKER.length));
      return {
        date: normalize_text(parsed?.date),
        body: normalize_text(parsed?.body),
      };
    } catch {
      return null;
    }
  }

  return null;
}

function parse_further_action_payload(journal = {}) {
  const raw_description = normalize_text(journal?.journal_description);
  if (raw_description.startsWith(FURTHER_ACTION_MARKER)) {
    try {
      const parsed = JSON.parse(raw_description.slice(FURTHER_ACTION_MARKER.length));
      return {
        title: normalize_text(parsed?.title),
        correspondence: normalize_text(parsed?.correspondence),
        status: normalize_text(parsed?.status),
        reminder_at: normalize_text(parsed?.reminder_at),
      };
    } catch {
      return null;
    }
  }

  return null;
}

function actor_variant_from_actor(actor) {
  const normalized_actor = normalize_text(actor).toLowerCase();
  if (normalized_actor.includes('applicant')) {
    return 'applicant';
  }
  if (normalized_actor.includes('respond')) {
    return 'respondent';
  }
  if (normalized_actor.includes('court')) {
    return 'court';
  }
  return 'neutral';
}

function matter_variant_from_status(status) {
  const normalized_status = normalize_text(status).toLowerCase();

  if (normalized_status.includes('resolv') || normalized_status.includes('final')) {
    return 'success';
  }

  if (normalized_status.includes('stuck') || normalized_status.includes('struck') || normalized_status.includes('hold')) {
    return 'warning';
  }

  return 'neutral';
}

function normalize_matter_status(status) {
  const normalized_status = normalize_text(status).toLowerCase();

  if (normalized_status.includes('resolv') || normalized_status.includes('final')) {
    return 'Resolved';
  }

  if (normalized_status.includes('stuck') || normalized_status.includes('struck') || normalized_status.includes('hold')) {
    return 'Stuck';
  }

  return 'Pending';
}

function normalize_further_action_status(status) {
  const normalized_status = normalize_text(status).toLowerCase();
  if (normalized_status.includes('resolv') || normalized_status.includes('done')) {
    return 'Resolved';
  }
  if (normalized_status.includes('urgent')) {
    return 'Urgent';
  }
  return 'Pending';
}

function further_action_variant_from_status(status) {
  return normalize_further_action_status(status) === 'Resolved' ? 'success' : 'warning';
}

function format_datetime_local(date = new Date()) {
  const normalized_date = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(normalized_date.getTime())) {
    return '';
  }

  const offset_ms = normalized_date.getTimezoneOffset() * 60 * 1000;
  return new Date(normalized_date.getTime() - offset_ms).toISOString().slice(0, 16);
}

function urgent_further_action_reminder_value() {
  return format_datetime_local(new Date(Date.now() + 24 * 60 * 60 * 1000));
}

function reminder_value_for_further_action_status(status = '', reminder_at = '') {
  if (normalize_further_action_status(status) !== 'Urgent') {
    return normalize_text(reminder_at);
  }

  return normalize_text(reminder_at) || urgent_further_action_reminder_value();
}

function reminder_date_from_value(value = '') {
  const normalized = normalize_text(value);
  if (!normalized) {
    return null;
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function format_reminder(value = '') {
  const parsed = reminder_date_from_value(value);
  if (!parsed) {
    return '';
  }

  return parsed.toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function is_due_reminder(value = '') {
  const parsed = reminder_date_from_value(value);
  return Boolean(parsed && parsed.getTime() <= Date.now());
}

function map_pleadings_from_journals(journals = []) {
  return journals
    .map((journal) => {
      const filing_payload = parse_filing_payload(journal);
      if (!filing_payload?.title) {
        return null;
      }

      return {
        id: Number(journal?.id || 0),
        date_raw: normalize_text(journal?.journal_date || ''),
        date: format_date(journal?.journal_date || journal?.date_created),
        actor: filing_payload.actor || 'Other',
        actor_variant: actor_variant_from_actor(filing_payload.actor),
        title: filing_payload.title,
        subtitle: filing_payload.description,
      };
    })
    .filter(Boolean);
}

function map_interlocutory_matters_from_journals(journals = []) {
  return journals
    .map((journal) => {
      const matter_payload = parse_matter_payload(journal);
      if (!matter_payload?.title) {
        return null;
      }

      const matter_status = normalize_matter_status(matter_payload.status);

      return {
        id: Number(journal?.id || 0),
        date_raw: normalize_text(journal?.journal_date || ''),
        date: format_date(journal?.journal_date || journal?.date_created),
        status: matter_status,
        status_variant: matter_variant_from_status(matter_status),
        title: matter_payload.title,
        subtitle: matter_payload.description,
      };
    })
    .filter(Boolean);
}

function map_judge_notes_from_journals(journals = []) {
  return journals
    .map((journal) => {
      const note_payload = parse_judge_note_payload(journal);
      if (!note_payload?.body) {
        return null;
      }

      return {
        id: Number(journal?.id || 0),
        date_raw: normalize_text(journal?.journal_date || ''),
        date: format_date(journal?.journal_date || journal?.date_created),
        body: note_payload.body,
      };
    })
    .filter(Boolean);
}

function map_further_actions_from_journals(journals = []) {
  return journals
    .map((journal) => {
      const action_payload = parse_further_action_payload(journal);
      if (!action_payload?.title) {
        return null;
      }

      const status = normalize_further_action_status(action_payload.status);

      return {
        id: Number(journal?.id || 0),
        date_raw: normalize_text(journal?.journal_date || ''),
        date: format_date(journal?.journal_date || journal?.date_created),
        title: action_payload.title,
        correspondence: action_payload.correspondence,
        status,
        status_variant: further_action_variant_from_status(status),
        reminder_at: action_payload.reminder_at,
        reminder_label: format_reminder(action_payload.reminder_at),
        reminder_due: ['Pending', 'Urgent'].includes(status) && is_due_reminder(action_payload.reminder_at),
      };
    })
    .filter(Boolean);
}

function find_backend_sub_section(backend_sections = [], aliases = []) {
  const normalized_aliases = aliases.map(normalize_label).filter(Boolean);
  const sub_sections = (Array.isArray(backend_sections) ? backend_sections : [])
    .flatMap((main_section) => main_section?.sub_sections || []);

  return sub_sections.find((section) => {
    const section_name = normalize_label(section?.name);
    return normalized_aliases.some((alias) =>
      section_name.includes(alias) || alias.includes(section_name)
    );
  }) || null;
}

function find_section_question_answer(section = {}, aliases = []) {
  const normalized_aliases = aliases.map(normalize_label).filter(Boolean);
  const questions = Array.isArray(section?.questions) ? section.questions : [];
  const matching_questions = questions.filter((question) => {
    const question_label = normalize_label(question?.question_text);
    return normalized_aliases.some((alias) =>
      question_label.includes(alias) || alias.includes(question_label)
    );
  });

  return normalize_text(
    (matching_questions.find((question) => normalize_text(question?.answer)) || matching_questions[0])?.answer
  );
}

function parse_backend_section_payload_rows(section = {}, aliases = [], parse_payload = (block) => block, build_row = (payload, index) => ({ index, payload }), fallback_row_builder = () => []) {
  const raw_value = find_section_question_answer(section, aliases);
  const parsed_rows = parse_serialized_payload_stream(raw_value, parse_payload)
    .map(({ payload, index }) => build_row(payload, index))
    .filter(Boolean);

  if (parsed_rows.length) {
    return parsed_rows;
  }

  const fallback_rows = fallback_row_builder(section);
  return Array.isArray(fallback_rows) ? fallback_rows.filter(Boolean) : [];
}

function map_pleadings_from_backend_sections(backend_sections = []) {
  const section = find_backend_sub_section(backend_sections, ['pleadings', 'notices']);
  if (!section) {
    return [];
  }

  return parse_backend_section_payload_rows(
    section,
    ['description', 'title', 'pleading', 'notice'],
    (block) => parse_filing_payload({ journal_description: block }),
    (payload, index) => ({
      id: section.id || `pleadings-api-${index}`,
      date_raw: normalize_text(payload?.date),
      date: format_date(payload?.date) || normalize_text(payload?.date),
      actor: normalize_text(payload?.actor) || 'Court',
      actor_variant: actor_variant_from_actor(payload?.actor || 'Court'),
      title: normalize_text(payload?.title) || 'Pleading / Notice',
      subtitle: normalize_text(payload?.description),
      index,
    }),
    (fallback_section) => {
      const filing_date = find_section_question_answer(fallback_section, ['date', 'filing date']);
      const description = find_section_question_answer(fallback_section, ['description', 'notice', 'pleading']);
      if (!filing_date && !description) {
        return [];
      }

      return [{
        id: fallback_section.id || 'pleadings-api',
        date_raw: filing_date,
        date: format_date(filing_date) || filing_date,
        actor: 'Court',
        actor_variant: 'court',
        title: description || 'Pleading / Notice',
        subtitle: description,
        index: 0,
      }];
    }
  );
}

function map_interlocutory_matters_from_backend_sections(backend_sections = []) {
  const section = find_backend_sub_section(backend_sections, ['interlocutory matters', 'interlocutory']);
  if (!section) {
    return [];
  }

  return parse_backend_section_payload_rows(
    section,
    ['description', 'title', 'matter'],
    (block) => parse_matter_payload({ journal_description: block }),
    (payload, index) => {
      const status = normalize_matter_status(payload?.status || 'Pending');
      return {
        id: section.id || `matter-api-${index}`,
        date_raw: normalize_text(payload?.date),
        date: format_date(payload?.date) || normalize_text(payload?.date),
        status,
        status_variant: matter_variant_from_status(status),
        title: normalize_text(payload?.title) || 'Interlocutory matter',
        subtitle: normalize_text(payload?.description),
        index,
      };
    },
    (fallback_section) => {
      const matter_date = find_section_question_answer(fallback_section, ['date of matter', 'matter date', 'date']);
      const description = find_section_question_answer(fallback_section, ['description of matter', 'description', 'matter']);
      if (!matter_date && !description) {
        return [];
      }

      return [{
        id: fallback_section.id || 'matter-api',
        date_raw: matter_date,
        date: format_date(matter_date) || matter_date,
        status: 'Pending',
        status_variant: 'warning',
        title: description || 'Interlocutory matter',
        subtitle: description,
        index: 0,
      }];
    }
  );
}

function map_judge_notes_from_backend_sections(backend_sections = []) {
  const section = find_backend_sub_section(backend_sections, ['judge', 'notes']);
  if (!section) {
    return [];
  }

  return parse_backend_section_payload_rows(
    section,
    ["enter judge's notes", "judge's notes", 'notes', 'note', 'body'],
    (block) => parse_judge_note_payload({ journal_description: block }),
    (payload, index) => ({
      id: section.id || `judge-note-api-${index}`,
      date_raw: normalize_text(payload?.date),
      date: format_date(payload?.date) || normalize_text(payload?.date),
      body: normalize_text(payload?.body),
      index,
    }),
    (fallback_section) => {
      const body = find_section_question_answer(fallback_section, ["enter judge's notes", "judge's notes", 'notes']);
      if (!body) {
        return [];
      }

      return [{
        id: fallback_section.id || 'judge-note-api',
        date_raw: '',
        date: '',
        body,
        index: 0,
      }];
    }
  );
}

function build_case_details({
  application_id,
  application_forms = [],
  complete_forms = [],
  raw_documents = [],
  journals = [],
  pending_case = null,
  profile_detail = null,
  saved_meta = {},
}) {
  const question_entries = merge_profile_answers_into_question_entries(
    flatten_questions(application_forms),
    profile_detail?.form_answers
  );
  const backend_sections = complete_forms.length
    ? normalize_backend_case_sections(complete_forms, question_entries)
    : normalize_application_form_case_sections(application_forms);
  const documents = map_documents(raw_documents, saved_meta);
  const case_data_question_map = build_case_data_question_map(question_entries);
  const case_citation_question_entry = find_question_entry(
    question_entries,
    build_case_citation_match_config()
  );
  const forum_question_entry = find_question_entry(question_entries, ['forum']);
  const farm_question_entry = find_question_entry(question_entries, [
    'known or registered farm',
    'select from existing farms',
    'farm',
  ]);
  const town_question_entry = find_question_entry(question_entries, ['town district', 'district', 'town']);
  const municipality_question_entry = find_question_entry(question_entries, ['municipality']);
  const province_question_entry = find_question_entry(question_entries, ['province']);
  const case_number = first_non_empty_value([
    pending_case?.case_number,
    case_data_question_map.case_number?.answer,
    saved_meta?.case_number,
  ]);
  const farm_group = first_non_empty_value([
    case_data_question_map.known_or_registered_farm?.answer,
    farm_question_entry?.answer,
    pending_case?.group_name,
    saved_meta?.farm,
  ]);
  const forum = first_non_empty_value([
    forum_question_entry?.answer,
    pending_case?.location,
  ]);
  const case_citation = first_non_empty_value([
    case_citation_question_entry?.answer,
    pending_case?.case_name,
    saved_meta?.case_citation,
  ]);
  const case_data_fields = build_case_data_fields(case_data_question_map, {
    known_or_registered_farm: first_non_empty_value([
      case_data_question_map.known_or_registered_farm?.answer,
      farm_question_entry?.answer,
      pending_case?.group_name,
      saved_meta?.farm,
    ]),
    province: first_non_empty_value([
      case_data_question_map.province?.answer,
      province_question_entry?.answer,
      saved_meta?.province,
    ]),
    town_district: first_non_empty_value([
      case_data_question_map.town_district?.answer,
      town_question_entry?.answer,
      saved_meta?.town_district,
    ]),
    municipality: first_non_empty_value([
      case_data_question_map.municipality?.answer,
      municipality_question_entry?.answer,
      saved_meta?.municipality,
    ]),
    select_from_existing_farms: first_non_empty_value([
      case_data_question_map.select_from_existing_farms?.answer,
      saved_meta?.select_from_existing_farms,
    ]),
  });
  const parties_question_map = build_party_question_map(question_entries);
  const section_heading_map = build_section_heading_map({
    backend_sections,
    case_data_question_map,
    parties_question_map,
  });
  const applicants = first_non_empty_value([
    parties_question_map.applicants?.answer,
    pending_case?.applicants,
  ]);
  const respondents = first_non_empty_value([
    parties_question_map.respondents?.answer,
    pending_case?.respondents,
  ]);
  const applicants_legal_representatives = first_non_empty_value([
    parties_question_map.applicants_legal_representatives?.answer,
  ]);
  const respondents_legal_representatives = first_non_empty_value([
    parties_question_map.respondents_legal_representatives?.answer,
    pending_case?.legal_representation,
  ]);
  const parties_form = build_parties_form({
    applicants,
    applicants_legal_representatives,
    respondents,
    respondents_legal_representatives,
  });
  const applicants_legal_representatives_verified = join_text_blocks(
    parties_form.applicants_legal_representatives_verified
  );
  const respondents_legal_representatives_verified = join_text_blocks(
    parties_form.respondents_legal_representatives_verified
  );
  const raw_status_label = first_non_empty_value([
    pending_case?.status?.status,
    pending_case?.status?.name,
    pending_case?.status,
    profile_detail?.application_status,
    saved_meta?.status,
  ]);
  const hearing_date = first_non_empty_value([
    saved_meta?.hearing_date,
  ]);
  const status_label = build_status_label(raw_status_label, hearing_date);
  const last_filing = documents[0]?.date || '';
  const farm_title = [farm_group, forum].filter((value, index, array) => normalize_text(value) && array.indexOf(value) === index).join(', ');

  const backend_pleadings = map_pleadings_from_backend_sections(backend_sections);
  const backend_interlocutory_matters = map_interlocutory_matters_from_backend_sections(backend_sections);
  const backend_judges_notes = map_judge_notes_from_backend_sections(backend_sections);
  const pleadings = backend_pleadings.length
    ? backend_pleadings
    : map_pleadings_from_journals(journals);
  const interlocutory_matters = backend_interlocutory_matters.length
    ? backend_interlocutory_matters
    : map_interlocutory_matters_from_journals(journals);
  const further_actions = map_further_actions_from_journals(journals);
  const judges_notes = backend_judges_notes.length
    ? backend_judges_notes
    : map_judge_notes_from_journals(journals);

  return {
    application_id,
    farm_group,
    farm_title,
    farm_location: forum,
    case_number,
    case_citation,
    status_label,
    status_variant: status_variant_from_status(raw_status_label || status_label),
    last_filing,
    backend_sections,
    section_heading_map,
    case_data_fields,
    case_data: build_case_data_items(case_data_fields),
    case_data_form: {
      known_or_registered_farm: normalize_case_data_field_value('known_or_registered_farm', first_non_empty_value([
        case_data_question_map.known_or_registered_farm?.answer,
        farm_question_entry?.answer,
        pending_case?.group_name,
        saved_meta?.farm,
      ]), case_data_question_map.known_or_registered_farm),
      province: normalize_case_data_field_value('province', first_non_empty_value([
        case_data_question_map.province?.answer,
        province_question_entry?.answer,
        saved_meta?.province,
      ]), case_data_question_map.province),
      town_district: normalize_case_data_field_value('town_district', first_non_empty_value([
        case_data_question_map.town_district?.answer,
        town_question_entry?.answer,
        saved_meta?.town_district,
      ]), case_data_question_map.town_district),
      municipality: normalize_case_data_field_value('municipality', first_non_empty_value([
        case_data_question_map.municipality?.answer,
        municipality_question_entry?.answer,
        saved_meta?.municipality,
      ]), case_data_question_map.municipality),
      select_from_existing_farms: normalize_case_data_field_value('select_from_existing_farms', first_non_empty_value([
        case_data_question_map.select_from_existing_farms?.answer,
        saved_meta?.select_from_existing_farms,
      ]), case_data_question_map.select_from_existing_farms),
    },
    case_data_question_map,
    parties: {
      applicants,
      applicants_legal_representatives,
      applicants_legal_representatives_verified,
      respondents,
      respondents_legal_representatives,
      respondents_legal_representatives_verified,
    },
    parties_form,
    parties_question_map,
    pleadings,
    interlocutory_matters,
    further_actions,
    judges_notes,
    documents,
    hearing_date,
  };
}

function normalize_assigned_form_statuses(records = []) {
  const records_list = Array.isArray(records) ? records : [];
  return records_list.map((status, index) => normalize_assigned_form_status_record(status, index));
}

function normalize_assigned_form_status_record(status = {}, index = 0) {
  return {
    id: Number(first_non_empty_value([status?.id, status?.assigned_form_status_id, 0])),
    status_id: Number(first_non_empty_value([status?.status_id, status?.workflow_status_id, status?.id, 0])),
    status_name: first_non_empty_value([
      status?.status_name,
      status?.workflow_status_name,
      status?.status?.status,
      status?.status,
      status?.name,
      `Status ${index + 1}`,
    ]),
    transition_action: first_non_empty_value([
      status?.transition_action,
      status?.status?.transition_action,
    ]),
    order: Number(first_non_empty_value([status?.order, status?.order_number, index + 1])),
    is_initial: Boolean(status?.is_initial),
    is_final: Boolean(status?.is_final),
  };
}

function resolve_transition_status_object(rule = {}, direction = 'from') {
  return direction === 'to'
    ? (rule?.to_status || rule?.target_status || {})
    : (rule?.from_status || rule?.source_status || {});
}

function resolve_transition_status_id(rule = {}, direction = 'from') {
  const status_object = resolve_transition_status_object(rule, direction);
  const direct_status_id = direction === 'to'
    ? first_non_empty_value([rule?.to_status_id, rule?.target_status_id, rule?.targetId])
    : first_non_empty_value([rule?.from_status_id, rule?.source_status_id, rule?.sourceId]);

  return Number(first_non_empty_value([
    direct_status_id,
    status_object?.id,
    0,
  ]));
}

function find_assigned_status_by_id(assigned_form_statuses = [], status_id = 0) {
  return assigned_form_statuses.find((status) => Number(status?.id || 0) === Number(status_id || 0)) || null;
}

function resolve_transition_status_name(rule = {}, direction = 'from', assigned_status = null) {
  const status_object = resolve_transition_status_object(rule, direction);
  return first_non_empty_value([
    assigned_status?.status_name,
    status_object?.status_name,
    status_object?.status,
    status_object?.name,
    direction === 'to' ? rule?.to_status : rule?.from_status,
    direction === 'to' ? rule?.target : rule?.source,
  ]);
}

function resolve_transition_action(rule = {}, target_status = null) {
  const condition = rule?.condition || {};
  const first_rule = Array.isArray(condition?.rules) ? condition.rules[0] || {} : {};
  return normalize_text(first_non_empty_value([
    rule?.transition_action,
    rule?.action,
    rule?.action_name,
    rule?.action_description,
    rule?.to_status?.transition_action,
    target_status?.transition_action,
    condition?.action,
    first_rule?.action,
    'Move to next status',
  ]));
}

function resolve_transition_type(rule = {}) {
  return normalize_text(first_non_empty_value([
    rule?.transition_type?.name,
    rule?.transition_type,
    'Manual',
  ]));
}

function build_case_transition_record(rule, assigned_form_statuses = []) {
  const source_id = resolve_transition_status_id(rule, 'from');
  const target_id = resolve_transition_status_id(rule, 'to');
  const source_status = find_assigned_status_by_id(assigned_form_statuses, source_id);
  const target_status = find_assigned_status_by_id(assigned_form_statuses, target_id);
  const action = resolve_transition_action(rule, target_status);

  return {
    id: Number(rule?.id || 0),
    sourceId: source_id,
    targetId: target_id,
    source: resolve_transition_status_name(rule, 'from', source_status),
    target: resolve_transition_status_name(rule, 'to', target_status),
    targetWorkflowStatusId: Number(target_status?.status_id || 0),
    action,
    targetTransitionAction: normalize_text(target_status?.transition_action),
    transitionType: resolve_transition_type(rule) || 'Manual',
    isActive: Boolean(rule?.is_active ?? true),
    requiresHearingSchedule: normalize_label(action).includes('schedule hearing'),
  };
}

function build_status_match_criteria(pending_case = null, saved_meta = {}, case_details = {}) {
  return {
    form_status_id: Number(saved_meta?.workflow_form_status_id || 0),
    workflow_status_id: Number(pending_case?.status?.id || saved_meta?.workflow_status_id || 0),
    status_label: normalize_label(
      pending_case?.status?.status
      || pending_case?.status?.name
      || saved_meta?.status
      || case_details?.status_label
    ),
  };
}

function status_matches_criteria(status = {}, criteria = {}) {
  if (criteria.form_status_id && Number(status?.id || 0) === criteria.form_status_id) {
    return true;
  }

  if (criteria.workflow_status_id && Number(status?.status_id || 0) === criteria.workflow_status_id) {
    return true;
  }

  return Boolean(criteria.status_label)
    && normalize_label(status?.status_name) === criteria.status_label;
}

function find_matching_form_status(assigned_form_statuses = [], criteria = {}) {
  const valid_statuses = Array.isArray(assigned_form_statuses) ? assigned_form_statuses : [];
  const exact_match = valid_statuses.find((status) => status_matches_criteria(status, criteria));
  if (exact_match) {
    return exact_match;
  }

  return valid_statuses.find((status) => Boolean(status?.is_initial)) || valid_statuses[0] || null;
}

function build_workflow_context(application_form = null, pending_case = null, saved_meta = {}) {
  return {
    form_id: Number(application_form?.form?.id || 0),
    application_form_id: Number(application_form?.application_form_id || application_form?.id || 0),
    entity_profile_id: Number(
      saved_meta?.entity_profile_id
      || pending_case?.entity_profile?.id
      || pending_case?.entity_profile_id
      || 0
    ) || null,
  };
}

function extract_assigned_form_statuses(response = {}) {
  return normalize_assigned_form_statuses(
    response?.data?.assigned_statuses
    || response?.assigned_statuses
    || []
  );
}

function extract_transition_rules(response = {}) {
  if (Array.isArray(response?.data)) {
    return response.data;
  }

  if (Array.isArray(response?.data?.transition_rules)) {
    return response.data.transition_rules;
  }

  if (Array.isArray(response?.data?.rules)) {
    return response.data.rules;
  }

  if (Array.isArray(response?.transition_rules)) {
    return response.transition_rules;
  }

  if (Array.isArray(response?.rules)) {
    return response.rules;
  }

  return Array.isArray(response) ? response : [];
}

export default {
  name: 'ViewCaseDetails',
  mixins: [alert_mixin],
  data() {
    return {
      loading_case_details: false,
      saving_case_data: false,
      saving_parties: false,
      saving_filing: false,
      saving_matter: false,
      saving_note: false,
      saving_further_action: false,
      backend_filing_section_key: '',
      editing_backend_filing_section_key: '',
      editing_backend_filing_row_index: null,
      backend_matter_section_key: '',
      editing_backend_matter_section_key: '',
      editing_backend_matter_row_index: null,
      backend_matter_status_by_section: {},
      backend_note_section_key: '',
      editing_backend_note_section_key: '',
      editing_backend_note_row_index: null,
      updating_matter_status_id: null,
      updating_further_action_status_id: null,
      saving_document: false,
      loading_workflow_actions: false,
      transitioning_to_status_id: null,
      case_details_error: '',
      editing_case_data: false,
      show_parties_modal: false,
      show_add_parties_modal: false,
      show_schedule_hearing_modal: false,
      showing_add_filing: false,
      showing_add_matter: false,
      showing_add_note: false,
      showing_add_further_action: false,
      show_edit_filing_modal: false,
      show_edit_matter_modal: false,
      show_edit_note_modal: false,
      show_edit_further_action_modal: false,
      editing_filing_id: null,
      editing_matter_id: null,
      editing_note_id: null,
      editing_further_action_id: null,
      announced_further_action_reminder_ids: [],
      showing_add_document: false,
      selected_document: null,
      application_forms: [],
      backend_section_editing_key: '',
      backend_section_answer_values: {},
      saving_backend_section_key: '',
      case_workflow_context: {
        application_form_id: null,
        form_id: null,
        entity_profile_id: null,
      },
      assigned_form_statuses: [],
      current_assigned_form_status: null,
      available_case_transitions: [],
      pending_case_transition: null,
    case_details: build_empty_case_details(Number(this.$route?.params?.applicationId || 0)),
      case_data_form: build_default_case_data_form(),
      case_data_question_map: {},
      parties_form: build_default_parties_form(),
      add_parties_form: build_default_parties_form(),
      parties_question_map: {},
      filing_form: build_default_filing_form(),
      matter_form: build_default_matter_form(),
      note_form: build_default_note_form(),
      further_action_form: build_default_further_action_form(),
      hearing_schedule_form: build_default_hearing_schedule_form(),
      document_form: build_default_document_form(),
    };
  },
  computed: {
    case_status_badge_class() {
      return {
        'land-court-status-warning': this.case_details.status_variant === 'warning',
        'land-court-status-success': this.case_details.status_variant === 'success',
        'land-court-status-hearing': this.case_details.status_variant === 'hearing',
        'land-court-neutral-chip': this.case_details.status_variant === 'neutral',
      };
    },
    editable_case_data_fields() {
      const existing_fields_by_key = new Map(
        (Array.isArray(this.case_details.case_data_fields) ? this.case_details.case_data_fields : [])
          .map((field) => [field.key, field])
      );

      return CASE_DATA_FIELD_KEYS
        .filter((field_key) => has_editable_case_data_question(this.case_data_question_map, field_key))
        .map((field_key) => build_editable_case_data_field(field_key, this.case_data_form, existing_fields_by_key));
    },
    case_data_section_heading() {
      return this.case_detail_section_heading('case_data');
    },
    has_case_data_section() {
      return Boolean(this.case_data_section_heading);
    },
    parties_section_heading() {
      return this.case_detail_section_heading('parties');
    },
    has_parties_section() {
      return Boolean(this.parties_section_heading);
    },
    pleadings_section_heading() {
      return this.case_detail_section_heading('pleadings');
    },
    has_pleadings_section() {
      return Boolean(this.pleadings_section_heading);
    },
    interlocutory_matters_section_heading() {
      return this.case_detail_section_heading('interlocutory_matters');
    },
    has_interlocutory_matters_section() {
      return Boolean(this.interlocutory_matters_section_heading);
    },
    further_actions_section_heading() {
      return this.case_detail_section_heading('further_actions', 'Further Actions');
    },
    has_further_actions_section() {
      return Boolean(this.further_actions_section_heading);
    },
    judges_notes_section_heading() {
      return this.case_detail_section_heading('judges_notes');
    },
    has_judges_notes_section() {
      return Boolean(this.judges_notes_section_heading);
    },
    documents_section_heading() {
      return this.case_detail_section_heading('documents', 'Documents');
    },
    visible_case_transitions() {
      return (Array.isArray(this.available_case_transitions) ? this.available_case_transitions : [])
        .filter((transition) =>
          transition?.isActive !== false
          && !String(transition?.transitionType || '').toLowerCase().includes('automatic')
        );
    },
    backend_case_sections() {
      return flatten_backend_case_sections_for_render(this.case_details)
        .filter((section) => !is_documents_section_name(section?.name));
    },
    due_further_action_reminders() {
      return (Array.isArray(this.case_details.further_actions) ? this.case_details.further_actions : [])
        .filter((item) => item?.reminder_due);
    },
  },
  created() {
    this.load_case_details();
  },
  methods: {
    backend_question_key(section = {}, question = {}) {
      return [
        section?.backend_key,
        question?.section_question_id,
        question?.id,
        question?.question_text,
      ].map((value) => String(value ?? '')).join(':');
    },
    backend_question_answer(question = {}) {
      const answer = question?.answer;
      if (answer !== null && answer !== undefined && String(answer).trim()) {
        return String(answer);
      }

      if (question?.presigned_url) {
        return String(question.presigned_url);
      }

      return 'Not Provided';
    },
    backend_question_type(question = {}) {
      return normalize_label(question?.question_type?.question_type || question?.question_type);
    },
    backend_question_choices(question = {}) {
      if (Array.isArray(question?.multi_select_choices) && question.multi_select_choices.length) {
        return question.multi_select_choices;
      }

      if (Array.isArray(question?.options)) {
        return question.options;
      }

      return [];
    },
    is_backend_select_question(question = {}) {
      return this.backend_question_type(question).includes('select');
    },
    is_backend_long_text_question(question = {}) {
      const question_type = this.backend_question_type(question);
      return question_type.includes('long') || question_type.includes('textarea') || question_type.includes('text area');
    },
    is_backend_file_question(question = {}) {
      return this.backend_question_type(question).includes('file');
    },
    backend_question_input_type(question = {}) {
      const question_type = this.backend_question_type(question);
      if (question_type.includes('date')) {
        return 'date';
      }
      if (question_type.includes('number')) {
        return 'number';
      }
      return 'text';
    },
    backend_section_has_answers(section = {}) {
      return (section.questions || []).some((question) => normalize_text(question?.answer));
    },
    backend_appendable_section_storage_question(section = {}, aliases = []) {
      const questions = Array.isArray(section?.questions) ? section.questions : [];
      const alias_match = this.backend_question_by_aliases(section, aliases);
      if (alias_match) {
        return alias_match;
      }

      const non_date_question = questions.find((question) =>
        !this.backend_question_type(question).includes('date')
      );

      return non_date_question || questions[0] || null;
    },
    backend_filing_payload_from_section(section = {}) {
      const questions = Array.isArray(section?.questions) ? section.questions : [];
      const date_question = questions.find((question) =>
        this.backend_question_type(question).includes('date')
        || normalize_label(question?.question_text).includes('date')
      );
      const title_question = this.backend_question_by_aliases(section, ['description', 'title', 'pleading', 'notice']);
      const actor_question = this.backend_question_by_aliases(section, ['filing party', 'party', 'actor']);

      return {
        date: normalize_text(date_question?.answer).slice(0, 10),
        title: normalize_text(title_question?.answer),
        actor: normalize_text(actor_question?.answer) || 'Court',
        description: normalize_text(title_question?.answer),
      };
    },
    backend_matter_payload_from_section(section = {}) {
      const questions = Array.isArray(section?.questions) ? section.questions : [];
      const date_question = questions.find((question) =>
        this.backend_question_type(question).includes('date')
        || normalize_label(question?.question_text).includes('date')
      );
      const description_question = this.backend_question_by_aliases(section, ['description', 'title', 'matter']);
      const status_question = this.backend_question_by_aliases(section, ['status']);

      return {
        date: normalize_text(date_question?.answer).slice(0, 10),
        title: normalize_text(description_question?.answer),
        status: normalize_matter_status(status_question?.answer),
        description: normalize_text(description_question?.answer),
      };
    },
    backend_note_payload_from_section(section = {}) {
      const questions = Array.isArray(section?.questions) ? section.questions : [];
      const date_question = questions.find((question) =>
        this.backend_question_type(question).includes('date')
        || normalize_label(question?.question_text).includes('date')
      );
      const note_question = this.backend_question_by_aliases(section, ["enter judge's notes", "judge's notes", 'notes', 'note', 'body']);

      return {
        date: normalize_text(date_question?.answer).slice(0, 10),
        body: normalize_text(note_question?.answer),
      };
    },
    backend_section_layout(section = {}) {
      const section_name = normalize_label(section?.name);
      if (section_name.includes('farm')) {
        return 'case_data';
      }
      if (section_name.includes('parties') || section_name.includes('represent')) {
        return 'party_cards';
      }
      if (
        section_name.includes('pleadings')
        || section_name.includes('notices')
        || section_name.includes('interlocutory')
        || section_name.includes('judge')
        || section_name.includes('notes')
      ) {
        return 'list';
      }
      return 'grid';
    },
    backend_section_primary_action(section = {}) {
      const section_name = normalize_label(section?.name);
      if (section_name.includes('farm')) {
        return this.case_details.case_data.length
          ? { label: 'Edit', icon: 'bi-pencil-square' }
          : { label: 'Add', icon: 'bi-plus-square' };
      }
      if (section_name.includes('pleadings') || section_name.includes('notices')) {
        return { label: 'Add filing', icon: 'bi-plus-square' };
      }
      if (section_name.includes('interlocutory')) {
        return { label: 'Add matter', icon: 'bi-plus-square' };
      }
      if (section_name.includes('judge') || section_name.includes('notes')) {
        return { label: 'Add note', icon: 'bi-plus-square' };
      }
      if (is_documents_section_name(section_name)) {
        return { label: 'Add document', icon: 'bi-plus-square' };
      }
      if (section_name.includes('parties') || section_name.includes('represent')) {
        return { label: 'Edit', icon: 'bi-pencil-square' };
      }
      return this.backend_section_has_answers(section)
        ? { label: 'Edit', icon: 'bi-pencil-square' }
        : { label: 'Add', icon: 'bi-plus-square' };
    },
    is_backend_pleadings_section(section = {}) {
      const section_name = normalize_label(section?.name);
      return section_name.includes('pleadings') || section_name.includes('notices');
    },
    is_backend_interlocutory_section(section = {}) {
      return normalize_label(section?.name).includes('interlocutory');
    },
    is_backend_judge_notes_section(section = {}) {
      const section_name = normalize_label(section?.name);
      return section_name.includes('judge') || section_name.includes('notes');
    },
    is_backend_farm_details_section(section = {}) {
      return normalize_label(section?.name).includes('farm');
    },
    handle_backend_section_primary_action(section = {}) {
      if (this.is_backend_farm_details_section(section)) {
        this.begin_case_data_edit();
        return;
      }

      if (this.is_backend_pleadings_section(section)) {
        this.open_backend_add_filing(section);
        return;
      }

      if (this.is_backend_interlocutory_section(section)) {
        this.open_backend_add_matter(section);
        return;
      }

      if (this.is_backend_judge_notes_section(section)) {
        this.open_backend_add_note(section);
        return;
      }

      this.begin_backend_section_edit(section);
    },
    handle_backend_list_row_edit(section = {}, row = {}) {
      if (this.is_backend_farm_details_section(section)) {
        this.begin_case_data_edit();
        return;
      }

      if (this.is_backend_pleadings_section(section)) {
        this.edit_backend_filing(section, row);
        return;
      }

      if (this.is_backend_interlocutory_section(section)) {
        this.edit_backend_matter(section, row);
        return;
      }

      if (this.is_backend_judge_notes_section(section)) {
        this.edit_backend_note(section, row);
        return;
      }

      this.begin_backend_section_edit(section);
    },
    backend_section_application_form_id(section = {}) {
      return Number(section?.application_form_id || (
        this.application_forms.find((application_form) =>
          String(section.backend_key || '').startsWith(String(application_form?.application_form_id || ''))
        )?.application_form_id || 0
      ));
    },
    backend_question_by_aliases(section = {}, aliases = []) {
      const normalized_aliases = aliases.map(normalize_label).filter(Boolean);
      return (section.questions || []).find((question) => {
        const question_label = normalize_label(question?.question_text);
        return normalized_aliases.some((alias) =>
          question_label.includes(alias) || alias.includes(question_label)
        );
      }) || null;
    },
    backend_filing_answer_payload(section = {}) {
      const storage_question = this.backend_appendable_section_storage_question(
        section,
        ['description', 'title', 'pleading', 'notice']
      );
      if (!storage_question?.section_question_id) {
        return [];
      }

      const existing_payloads = parse_serialized_payload_stream(
        storage_question?.answer,
        (block) => parse_filing_payload({ journal_description: block })
      ).map((entry) => entry.payload);
      if (!existing_payloads.length) {
        const legacy_payload = this.backend_filing_payload_from_section(section);
        if (legacy_payload.title) {
          existing_payloads.push(legacy_payload);
        }
      }

      const next_payload = {
        date: normalize_text(this.filing_form.filing_date).slice(0, 10),
        title: normalize_text(this.filing_form.title),
        actor: normalize_text(this.filing_form.actor) || 'Court',
        description: normalize_text(this.filing_form.description),
      };
      const next_index = Number.isInteger(this.editing_backend_filing_row_index)
        ? this.editing_backend_filing_row_index
        : null;
      const next_payloads = [...existing_payloads];
      if (next_index !== null && next_index >= 0 && next_index < next_payloads.length) {
        next_payloads[next_index] = next_payload;
      } else {
        next_payloads.push(next_payload);
      }

      return [{
        section_question_id: Number(storage_question.section_question_id),
        answer_text: join_serialized_payload_stream(
          next_payloads.map((payload) => serialize_filing_payload(payload))
        ),
      }];
    },
    open_backend_add_filing(section = {}) {
      this.backend_filing_section_key = section.backend_key;
      this.editing_backend_filing_section_key = '';
      this.editing_backend_filing_row_index = null;
      this.showing_add_filing = true;
      this.show_edit_filing_modal = false;
      this.filing_form = build_default_filing_form();
      this.case_details_error = '';
    },
    cancel_backend_filing() {
      if (this.saving_filing) {
        return;
      }

      this.backend_filing_section_key = '';
      this.editing_backend_filing_section_key = '';
      this.editing_backend_filing_row_index = null;
      this.showing_add_filing = false;
      this.show_edit_filing_modal = false;
      this.filing_form = build_default_filing_form();
    },
    edit_backend_filing(section = {}, row = {}) {
      this.backend_filing_section_key = '';
      this.editing_backend_filing_section_key = section.backend_key;
      this.editing_backend_filing_row_index = Number.isInteger(Number(row?.entry_index)) ? Number(row.entry_index) : Number(row?.index || 0);
      this.showing_add_filing = false;
      this.show_edit_filing_modal = true;
      this.filing_form = {
        filing_date: normalize_text(row?.date_raw || row?.date || '').slice(0, 10) || new Date().toISOString().slice(0, 10),
        title: normalize_text(row?.title),
        actor: normalize_text(row?.badge) || 'Court',
        description: normalize_text(row?.subtitle),
      };
      this.case_details_error = '';
    },
    async save_backend_filing(section = {}) {
      const application_form_id = this.backend_section_application_form_id(section);
      const answers = this.backend_filing_answer_payload(section);

      if (!application_form_id || !answers.length) {
        this.show_error('No backend-linked filing fields were found for this section.');
        return;
      }

      this.saving_filing = true;
      this.case_details_error = '';

      try {
        await submit_land_court_form_answers({
          application_form_id,
          current_section_index: Math.max(Number(section?.order_number || 1) - 1, 0),
          answers,
        });

        this.backend_filing_section_key = '';
        this.editing_backend_filing_section_key = '';
        this.editing_backend_filing_row_index = null;
        this.showing_add_filing = false;
        this.show_edit_filing_modal = false;
        this.filing_form = build_default_filing_form();
        clear_caseload_snapshots();
        await this.refresh_case_details();
        this.show_success('Filing saved successfully.');
      } catch (error) {
        this.show_action_error(error, 'Failed to save the filing.');
      } finally {
        this.saving_filing = false;
      }
    },
    backend_matter_answer_payload(section = {}) {
      const storage_question = this.backend_appendable_section_storage_question(
        section,
        ['description', 'title', 'matter']
      );
      if (!storage_question?.section_question_id) {
        return [];
      }

      const existing_payloads = parse_serialized_payload_stream(
        storage_question?.answer,
        (block) => parse_matter_payload({ journal_description: block })
      ).map((entry) => entry.payload);
      if (!existing_payloads.length) {
        const legacy_payload = this.backend_matter_payload_from_section(section);
        if (legacy_payload.title) {
          existing_payloads.push(legacy_payload);
        }
      }

      const next_payload = {
        date: normalize_text(this.matter_form.matter_date).slice(0, 10),
        title: normalize_text(this.matter_form.title),
        status: normalize_matter_status(this.matter_form.status),
        description: normalize_text(this.matter_form.description),
      };
      const next_index = Number.isInteger(Number(this.editing_backend_matter_row_index))
        ? Number(this.editing_backend_matter_row_index)
        : null;
      const next_payloads = [...existing_payloads];
      if (next_index !== null && next_index >= 0 && next_index < next_payloads.length) {
        next_payloads[next_index] = next_payload;
      } else {
        next_payloads.push(next_payload);
      }

      return [{
        section_question_id: Number(storage_question.section_question_id),
        answer_text: join_serialized_payload_stream(
          next_payloads.map((payload) => serialize_matter_payload(payload))
        ),
      }];
    },
    open_backend_add_matter(section = {}) {
      this.backend_matter_section_key = section.backend_key;
      this.editing_backend_matter_section_key = '';
      this.editing_backend_matter_row_index = null;
      this.showing_add_matter = true;
      this.show_edit_matter_modal = false;
      this.matter_form = build_default_matter_form();
      this.case_details_error = '';
    },
    cancel_backend_matter() {
      if (this.saving_matter) {
        return;
      }

      this.backend_matter_section_key = '';
      this.editing_backend_matter_section_key = '';
      this.editing_backend_matter_row_index = null;
      this.showing_add_matter = false;
      this.show_edit_matter_modal = false;
      this.matter_form = build_default_matter_form();
    },
    edit_backend_matter(section = {}, row = {}) {
      this.backend_matter_section_key = '';
      this.editing_backend_matter_section_key = section.backend_key;
      this.editing_backend_matter_row_index = Number.isInteger(Number(row?.entry_index)) ? Number(row.entry_index) : Number(row?.index || 0);
      this.showing_add_matter = false;
      this.show_edit_matter_modal = true;
      this.matter_form = {
        matter_date: normalize_text(row?.date_raw || row?.date || '').slice(0, 10) || new Date().toISOString().slice(0, 10),
        title: normalize_text(row?.title),
        status: normalize_matter_status(row?.status || row?.badge),
        description: normalize_text(row?.subtitle),
      };
      this.case_details_error = '';
    },
    async save_backend_matter(section = {}) {
      const application_form_id = this.backend_section_application_form_id(section);
      const answers = this.backend_matter_answer_payload(section);

      if (!application_form_id || !answers.length) {
        this.show_error('No backend-linked matter fields were found for this section.');
        return;
      }

      this.saving_matter = true;
      this.case_details_error = '';

      try {
        await submit_land_court_form_answers({
          application_form_id,
          current_section_index: Math.max(Number(section?.order_number || 1) - 1, 0),
          answers,
        });

        this.backend_matter_section_key = '';
        this.editing_backend_matter_section_key = '';
        this.editing_backend_matter_row_index = null;
        this.showing_add_matter = false;
        this.show_edit_matter_modal = false;
        this.matter_form = build_default_matter_form();
        clear_caseload_snapshots();
        await this.refresh_case_details();
        this.show_success('Matter saved successfully.');
      } catch (error) {
        this.show_action_error(error, 'Failed to save the matter.');
      } finally {
        this.saving_matter = false;
      }
    },
    async update_backend_matter_status(section = {}, row = {}, next_status = '') {
      const normalized_status = normalize_matter_status(next_status);
      const status_question = this.backend_question_by_aliases(section, ['status']);
      const application_id = Number(this.$route?.params?.applicationId || this.case_details?.application_id || 0);

      if (!status_question?.section_question_id && application_id && section?.backend_key) {
        const all_meta = parse_saved_case_meta();
        const current_meta = all_meta[String(application_id)] || {};
        const next_statuses = {
          ...(current_meta.backend_matter_status_by_section || {}),
          [section.backend_key]: normalized_status,
        };

        persist_case_meta({
          ...all_meta,
          [String(application_id)]: {
            ...current_meta,
            backend_matter_status_by_section: next_statuses,
          },
        });
        this.backend_matter_status_by_section = next_statuses;
      }

      this.matter_form = {
        matter_date: normalize_text(row?.date_raw || '').slice(0, 10) || new Date().toISOString().slice(0, 10),
        title: normalize_text(row?.title),
        status: normalized_status,
        description: normalize_text(row?.subtitle),
      };
      await this.save_backend_matter(section);
    },
    backend_note_answer_payload(section = {}) {
      const storage_question = this.backend_appendable_section_storage_question(
        section,
        ["enter judge's notes", "judge's notes", 'notes', 'note', 'body']
      );
      if (!storage_question?.section_question_id) {
        return [];
      }

      const existing_payloads = parse_serialized_payload_stream(
        storage_question?.answer,
        (block) => parse_judge_note_payload({ journal_description: block })
      ).map((entry) => entry.payload);
      if (!existing_payloads.length) {
        const legacy_payload = this.backend_note_payload_from_section(section);
        if (legacy_payload.body) {
          existing_payloads.push(legacy_payload);
        }
      }

      const next_payload = {
        date: normalize_text(this.note_form.note_date).slice(0, 10),
        body: normalize_text(this.note_form.body),
      };
      const next_index = Number.isInteger(Number(this.editing_backend_note_row_index))
        ? Number(this.editing_backend_note_row_index)
        : null;
      const next_payloads = [...existing_payloads];
      if (next_index !== null && next_index >= 0 && next_index < next_payloads.length) {
        next_payloads[next_index] = next_payload;
      } else {
        next_payloads.push(next_payload);
      }

      return [{
        section_question_id: Number(storage_question.section_question_id),
        answer_text: join_serialized_payload_stream(
          next_payloads.map((payload) => serialize_judge_note_payload(payload))
        ),
      }];
    },
    edit_backend_note(section = {}, row = {}) {
      this.backend_note_section_key = '';
      this.editing_backend_note_section_key = section.backend_key;
      this.editing_backend_note_row_index = Number.isInteger(Number(row?.entry_index)) ? Number(row.entry_index) : Number(row?.index || 0);
      this.showing_add_note = false;
      this.show_edit_note_modal = true;
      this.note_form = {
        note_date: normalize_text(row?.date_raw || row?.date || '').slice(0, 10) || new Date().toISOString().slice(0, 10),
        body: normalize_text(row?.title || row?.subtitle),
      };
      this.case_details_error = '';
    },
    open_backend_add_note(section = {}) {
      this.backend_note_section_key = section.backend_key;
      this.editing_backend_note_section_key = '';
      this.editing_backend_note_row_index = null;
      this.showing_add_note = true;
      this.show_edit_note_modal = false;
      this.note_form = build_default_note_form();
      this.case_details_error = '';
    },
    cancel_backend_note() {
      if (this.saving_note) {
        return;
      }

      this.backend_note_section_key = '';
      this.editing_backend_note_section_key = '';
      this.editing_backend_note_row_index = null;
      this.showing_add_note = false;
      this.show_edit_note_modal = false;
      this.note_form = build_default_note_form();
    },
    async save_backend_note(section = {}) {
      const application_form_id = this.backend_section_application_form_id(section);
      const answers = this.backend_note_answer_payload(section);

      if (!application_form_id || !answers.length) {
        this.show_error('No backend-linked judge note fields were found for this section.');
        return;
      }

      this.saving_note = true;
      this.case_details_error = '';

      try {
        await submit_land_court_form_answers({
          application_form_id,
          current_section_index: Math.max(Number(section?.order_number || 1) - 1, 0),
          answers,
        });

        this.backend_note_section_key = '';
        this.editing_backend_note_section_key = '';
        this.editing_backend_note_row_index = null;
        this.showing_add_note = false;
        this.show_edit_note_modal = false;
        this.note_form = build_default_note_form();
        clear_caseload_snapshots();
        await this.refresh_case_details();
        this.show_success('Judge note saved successfully.');
      } catch (error) {
        this.show_action_error(error, 'Failed to save the judge note.');
      } finally {
        this.saving_note = false;
      }
    },
    backend_party_groups(section = {}) {
      const questions = Array.isArray(section?.questions) ? section.questions : [];
      const applicant_questions = questions.filter((question) =>
        normalize_label(question?.question_text).includes('applicant')
      );
      const respondent_questions = questions.filter((question) =>
        normalize_label(question?.question_text).includes('respondent')
        || normalize_label(question?.question_text).includes('landowner')
      );
      const grouped_question_ids = new Set(
        [...applicant_questions, ...respondent_questions].map((question) => this.backend_question_key(section, question))
      );
      const other_questions = questions.filter((question) =>
        !grouped_question_ids.has(this.backend_question_key(section, question))
      );

      return [
        { title: 'Applicants', questions: applicant_questions },
        { title: 'Respondents', questions: respondent_questions },
        { title: 'Other', questions: other_questions },
      ].filter((group) => group.questions.length);
    },
    backend_section_rows(section = {}) {
      const questions = Array.isArray(section?.questions) ? section.questions : [];
      if (!questions.length) {
        return [];
      }

      const section_name = normalize_label(section?.name);
      if (section_name.includes('pleadings') || section_name.includes('notices')) {
        const storage_question = this.backend_appendable_section_storage_question(
          section,
          ['description', 'title', 'pleading', 'notice']
        );
        const parsed_rows = parse_serialized_payload_stream(
          storage_question?.answer,
          (block) => parse_filing_payload({ journal_description: block })
        )
          .map(({ payload, index }) => ({
            index,
            entry_index: index,
            date_raw: normalize_text(payload?.date),
            date: format_date(payload?.date) || normalize_text(payload?.date),
            badge: 'Court',
            badge_variant: 'court',
            status: '',
            title: normalize_text(payload?.title),
            subtitle: normalize_text(payload?.description),
            actor: normalize_text(payload?.actor) || 'Court',
          }))
          .filter((row) => row.title);

        if (parsed_rows.length) {
          return parsed_rows;
        }

        const legacy_payload = this.backend_filing_payload_from_section(section);
        return legacy_payload.title ? [{
          index: 0,
          entry_index: 0,
          date_raw: normalize_text(legacy_payload.date),
          date: format_date(legacy_payload.date) || normalize_text(legacy_payload.date),
          badge: 'Court',
          badge_variant: 'court',
          status: '',
          title: normalize_text(legacy_payload.title),
          subtitle: normalize_text(legacy_payload.description),
          actor: normalize_text(legacy_payload.actor) || 'Court',
        }] : [];
      }

      if (section_name.includes('interlocutory')) {
        const storage_question = this.backend_appendable_section_storage_question(
          section,
          ['description', 'title', 'matter']
        );
        const parsed_rows = parse_serialized_payload_stream(
          storage_question?.answer,
          (block) => parse_matter_payload({ journal_description: block })
        )
          .map(({ payload, index }) => {
            const matter_status = normalize_matter_status(payload?.status || this.backend_matter_status_by_section?.[section.backend_key] || 'Pending');
            return {
              index,
              entry_index: index,
              date_raw: normalize_text(payload?.date),
              date: format_date(payload?.date) || normalize_text(payload?.date),
              badge: matter_status,
              badge_variant: matter_variant_from_status(matter_status),
              status: matter_status,
              title: normalize_text(payload?.title),
              subtitle: normalize_text(payload?.description),
            };
          })
          .filter((row) => row.title);

        if (parsed_rows.length) {
          return parsed_rows;
        }

        const legacy_payload = this.backend_matter_payload_from_section(section);
        return legacy_payload.title ? [{
          index: 0,
          entry_index: 0,
          date_raw: normalize_text(legacy_payload.date),
          date: format_date(legacy_payload.date) || normalize_text(legacy_payload.date),
          badge: legacy_payload.status || normalize_matter_status(this.backend_matter_status_by_section?.[section.backend_key] || 'Pending'),
          badge_variant: matter_variant_from_status(legacy_payload.status || this.backend_matter_status_by_section?.[section.backend_key] || 'Pending'),
          status: normalize_matter_status(legacy_payload.status || this.backend_matter_status_by_section?.[section.backend_key] || 'Pending'),
          title: normalize_text(legacy_payload.title),
          subtitle: normalize_text(legacy_payload.description),
        }] : [];
      }

      if (section_name.includes('judge') || section_name.includes('notes')) {
        const storage_question = this.backend_appendable_section_storage_question(
          section,
          ["enter judge's notes", "judge's notes", 'notes', 'note', 'body']
        );
        const parsed_rows = parse_serialized_payload_stream(
          storage_question?.answer,
          (block) => parse_judge_note_payload({ journal_description: block })
        )
          .map(({ payload, index }) => ({
            index,
            entry_index: index,
            date_raw: normalize_text(payload?.date),
            date: format_date(payload?.date) || normalize_text(payload?.date),
            title: normalize_text(payload?.body),
            subtitle: '',
            body: normalize_text(payload?.body),
          }))
          .filter((row) => row.body);

        if (parsed_rows.length) {
          return parsed_rows;
        }

        const legacy_payload = this.backend_note_payload_from_section(section);
        return legacy_payload.body ? [{
          index: 0,
          entry_index: 0,
          date_raw: normalize_text(legacy_payload.date),
          date: format_date(legacy_payload.date) || normalize_text(legacy_payload.date),
          title: normalize_text(legacy_payload.body),
          subtitle: '',
          body: normalize_text(legacy_payload.body),
        }] : [];
      }

      const date_question = questions.find((question) =>
        this.backend_question_type(question).includes('date')
        || normalize_label(question?.question_text).includes('date')
      );
      const status_question = this.backend_question_by_aliases(section, ['status']);
      const description_question = questions.find((question) =>
        question !== date_question
        && question !== status_question
        && normalize_text(question?.answer)
      ) || questions.find((question) => question !== date_question && question !== status_question);
      const title = this.backend_question_answer(description_question || {});
      const row_date = date_question ? format_date(date_question.answer) || normalize_text(date_question.answer) : '';
      const matter_status = normalize_matter_status(
        status_question?.answer
        || this.backend_matter_status_by_section?.[section.backend_key]
        || 'Pending'
      );

      return [{
        index: 0,
        entry_index: 0,
        date_raw: normalize_text(date_question?.answer),
        date: row_date,
        badge: section_name.includes('interlocutory') ? matter_status : section_name.includes('pleadings') ? 'Court' : '',
        badge_variant: section_name.includes('interlocutory') ? matter_variant_from_status(matter_status) : 'court',
        status: section_name.includes('interlocutory') ? matter_status : '',
        title,
        subtitle: '',
      }];
    },
    is_editing_backend_section(section = {}) {
      return this.backend_section_editing_key === section?.backend_key;
    },
    backend_section_edit_value(question = {}) {
      const key = this.backend_question_key({ backend_key: this.backend_section_editing_key }, question);
      return this.backend_section_answer_values[key] ?? '';
    },
    begin_backend_section_edit(section = {}) {
      const next_values = {};
      (section.questions || []).forEach((question) => {
        next_values[this.backend_question_key(section, question)] = question?.answer ?? '';
      });

      this.backend_section_editing_key = section.backend_key;
      this.backend_section_answer_values = next_values;
      this.case_details_error = '';
    },
    cancel_backend_section_edit() {
      if (this.saving_backend_section_key) {
        return;
      }

      this.backend_section_editing_key = '';
      this.backend_section_answer_values = {};
    },
    update_backend_section_edit_value(question = {}, value = '') {
      const key = this.backend_question_key({ backend_key: this.backend_section_editing_key }, question);
      this.backend_section_answer_values = {
        ...this.backend_section_answer_values,
        [key]: value,
      };
    },
    update_backend_section_file_value(question = {}, files = []) {
      const [selected_file] = Array.from(files || []);
      const key = this.backend_question_key({ backend_key: this.backend_section_editing_key }, question);
      this.backend_section_answer_values = {
        ...this.backend_section_answer_values,
        [key]: selected_file || null,
      };
    },
    async save_backend_section(section = {}) {
      const application_form_id = Number(section?.application_form_id || (
        this.application_forms.find((application_form) =>
          String(section.backend_key || '').startsWith(String(application_form?.application_form_id || ''))
        )?.application_form_id || 0
      ));
      const section_index = Number(section?.order_number || 1) - 1;
      const answers = (section.questions || [])
        .map((question) => {
          const raw_value = this.backend_section_answer_values[this.backend_question_key(section, question)];
          const is_file_value = typeof File !== 'undefined' && raw_value instanceof File;
          const answer_text = is_file_value ? raw_value.name : raw_value;

          return {
            section_question_id: Number(question?.section_question_id || 0),
            answer_text: normalize_text(answer_text),
          };
        })
        .filter((answer) => answer.section_question_id && answer.answer_text);

      if (!application_form_id) {
        this.show_error('Missing application form id for this section.');
        return;
      }

      this.saving_backend_section_key = section.backend_key;
      this.case_details_error = '';

      try {
        await submit_land_court_form_answers({
          application_form_id,
          current_section_index: Math.max(section_index, 0),
          answers,
        });

        this.backend_section_editing_key = '';
        this.backend_section_answer_values = {};
        clear_caseload_snapshots();
        await this.refresh_case_details();
        this.show_success(`${section.name || 'Section'} saved successfully.`);
      } catch (error) {
        this.show_action_error(error, 'Failed to save this section.');
      } finally {
        this.saving_backend_section_key = '';
      }
    },
    backend_section_actions(section = {}) {
      const section_name = normalize_label(section?.name);
      if (section_name.includes('parties') || section_name.includes('representatives')) {
        return [
          {
            label: 'Add',
            icon: 'bi-plus-square',
            handler: () => this.open_add_parties_modal(),
          },
          {
            label: 'Edit',
            icon: 'bi-pencil-square',
            handler: () => this.open_parties_modal(),
          },
        ];
      }

      if (section_name.includes('pleadings') || section_name.includes('notices')) {
        return [{
          label: 'Add filing',
          icon: 'bi-plus-square',
          handler: () => this.open_add_filing(),
        }];
      }

      if (section_name.includes('interlocutory') || section_name.includes('matters')) {
        return [{
          label: 'Add matter',
          icon: 'bi-plus-square',
          handler: () => this.open_add_matter(),
        }];
      }

      if (section_name.includes('further') || section_name.includes('actions')) {
        return [{
          label: 'Add action',
          icon: 'bi-plus-square',
          handler: () => this.open_add_further_action(),
        }];
      }

      if (section_name.includes('judge') || section_name.includes('notes')) {
        return [{
          label: 'Add note',
          icon: 'bi-plus-square',
          handler: () => this.open_add_note(),
        }];
      }

      if (is_documents_section_name(section_name)) {
        return [{
          label: 'Add document',
          icon: 'bi-plus-square',
          handler: () => this.open_add_document(),
        }];
      }

      return this.backend_section_has_answers(section)
        ? [{
          label: 'Edit',
          icon: 'bi-pencil-square',
          handler: () => this.begin_backend_section_edit(section),
        }]
        : [{
          label: 'Add',
          icon: 'bi-plus-square',
          handler: () => this.begin_backend_section_edit(section),
        }];
    },
    alert_due_further_action_reminders() {
      const due_reminders = this.due_further_action_reminders.filter((item) =>
        !this.announced_further_action_reminder_ids.includes(Number(item?.id || 0))
      );

      if (!due_reminders.length) {
        return;
      }

      this.announced_further_action_reminder_ids = [
        ...this.announced_further_action_reminder_ids,
        ...due_reminders.map((item) => Number(item?.id || 0)),
      ];

      this.show_warning(
        'Further action reminder',
        due_reminders.length === 1
          ? due_reminders[0].title
          : `${due_reminders.length} pending further actions need attention.`
      );
    },
    reset_case_workflow_actions() {
      this.case_workflow_context = {
        application_form_id: null,
        form_id: null,
        entity_profile_id: null,
      };
      this.assigned_form_statuses = [];
      this.current_assigned_form_status = null;
      this.available_case_transitions = [];
    },
    resolve_case_application_form(saved_meta = {}) {
      const saved_application_form_id = Number(saved_meta?.application_form_id || 0);

      return this.application_forms.find(
        (application_form) => Number(application_form?.application_form_id || application_form?.id || 0) === saved_application_form_id
      ) || this.application_forms[0] || null;
    },
    find_current_assigned_form_status(assigned_form_statuses = [], pending_case = null, saved_meta = {}) {
      const criteria = build_status_match_criteria(
        pending_case,
        saved_meta,
        this.case_details
      );
      return find_matching_form_status(assigned_form_statuses, criteria);
    },
    should_load_case_workflow_actions(workflow_context = {}) {
      return Boolean(workflow_context?.form_id && workflow_context?.application_form_id);
    },
    async fetch_case_assigned_form_statuses(form_id) {
      const workflow_status_response = await fetch_assign_form_status_data({ form_id });
      return extract_assigned_form_statuses(workflow_status_response);
    },
    persist_case_workflow_status(application_id, workflow_context = {}, current_assigned_form_status = null) {
      const normalized_application_id = Number(application_id || 0);
      if (!normalized_application_id || !current_assigned_form_status) {
        return;
      }

      const case_meta_by_application_id = parse_saved_case_meta();
      const current_meta = case_meta_by_application_id[String(normalized_application_id)] || {};
      persist_case_meta({
        ...case_meta_by_application_id,
        [String(normalized_application_id)]: {
          ...current_meta,
          application_id: normalized_application_id,
          application_form_id: Number(workflow_context?.application_form_id || current_meta.application_form_id || 0) || null,
          entity_profile_id: Number(workflow_context?.entity_profile_id || current_meta.entity_profile_id || 0) || null,
          status: normalize_text(current_assigned_form_status?.status_name) || current_meta.status || '',
          workflow_form_status_id: Number(current_assigned_form_status?.id || 0) || null,
          workflow_status_id: Number(current_assigned_form_status?.status_id || 0) || null,
        },
      });
    },
    apply_current_case_status(status_name, hearing_date = '') {
      this.case_details = {
        ...this.case_details,
        status_label: build_status_label(status_name, hearing_date),
        status_variant: status_variant_from_status(status_name),
      };
    },
    async fetch_case_transition_records(form_id, current_assigned_form_status, assigned_form_statuses = []) {
      const transition_response = await fetch_form_workflow_transition_rules({
        form_id,
        from_status_id: current_assigned_form_status.id,
      });

      return extract_transition_rules(transition_response)
        .map((rule) => build_case_transition_record(rule, assigned_form_statuses))
        .filter((transition) => transition?.id && transition?.targetId);
    },
    case_detail_section_heading(section_key, fallback_label = '') {
      return normalize_text(this.case_details?.section_heading_map?.[section_key])
        || normalize_text(fallback_label);
    },
    has_case_detail_section(section_key) {
      return Boolean(this.case_detail_section_heading(section_key));
    },
    async load_case_workflow_actions(pending_case = null, saved_meta = {}) {
      const application_form = this.resolve_case_application_form(saved_meta);
      const workflow_context = build_workflow_context(application_form, pending_case, saved_meta);
      const application_id = this.case_details.application_id || this.$route?.params?.applicationId || '';

      this.reset_case_workflow_actions();
      if (!this.should_load_case_workflow_actions(workflow_context)) {
        return;
      }

      this.loading_workflow_actions = true;
      try {
        const assigned_form_statuses = await this.fetch_case_assigned_form_statuses(workflow_context.form_id);
        const current_assigned_form_status = this.find_current_assigned_form_status(
          assigned_form_statuses,
          pending_case,
          saved_meta
        );

        this.case_workflow_context = workflow_context;
        this.assigned_form_statuses = assigned_form_statuses;
        this.current_assigned_form_status = current_assigned_form_status;

        if (!current_assigned_form_status) {
          return;
        }

        this.persist_case_workflow_status(application_id, workflow_context, current_assigned_form_status);
        this.apply_current_case_status(current_assigned_form_status.status_name, saved_meta?.hearing_date);
        this.available_case_transitions = await this.fetch_case_transition_records(
          workflow_context.form_id,
          current_assigned_form_status,
          assigned_form_statuses
        );
      } catch (error) {
        console.error('Failed to load case workflow actions', error);
        this.reset_case_workflow_actions();
      } finally {
        this.loading_workflow_actions = false;
      }
    },
    action_error_message(error, fallback_message = 'Something went wrong.') {
      return error?.response?.data?.message
        || error?.response?.data?.detail
        || error?.message
        || fallback_message;
    },
    show_action_error(error, fallback_message = 'Something went wrong.') {
      this.show_error(this.action_error_message(error, fallback_message));
    },
    async refresh_case_details() {
      await this.load_case_details({ show_page_loader: false, show_page_error: false });
    },
    async load_case_details(options = {}) {
      const show_page_loader = options?.show_page_loader !== false;
      const show_page_error = options?.show_page_error !== false;
      const application_id = Number(this.$route?.params?.applicationId || 0);
      if (!application_id) {
        this.case_details_error = 'No application id was supplied for this case.';
        this.case_details = build_empty_case_details(0);
        return;
      }

      if (show_page_loader) {
        this.loading_case_details = true;
        this.case_details_error = '';
      }

      const saved_meta = parse_saved_case_meta()[String(application_id)] || {};
      this.backend_matter_status_by_section = saved_meta.backend_matter_status_by_section || {};

      try {
        const [
          application_forms,
          raw_documents,
          journals,
        ] = await Promise.all([
          fetch_land_court_application_forms(application_id),
          fetch_land_court_application_documents(application_id).catch(() => []),
          fetch_land_court_journals(application_id).catch(() => []),
        ]);

        this.application_forms = application_forms;
        this.case_details = build_case_details({
          application_id,
          application_forms,
          raw_documents,
          journals,
          saved_meta,
        });
        this.case_data_form = {
          ...build_default_case_data_form(),
          ...(this.case_details.case_data_form || {}),
        };
        this.case_data_question_map = this.case_details.case_data_question_map || {};
        this.parties_form = {
          ...build_default_parties_form(),
          ...(this.case_details.parties_form || {}),
        };
        this.parties_question_map = this.case_details.parties_question_map || {};
        await this.load_case_workflow_actions(null, saved_meta);
        this.alert_due_further_action_reminders();
      } catch (error) {
        const fallback_message = 'Failed to load this case from the backend.';
        if (show_page_error) {
          this.case_details_error = this.action_error_message(error, fallback_message);
          this.case_details = build_empty_case_details(application_id);
          this.case_data_form = build_default_case_data_form();
          this.case_data_question_map = {};
          this.parties_form = build_default_parties_form();
          this.parties_question_map = {};
          this.filing_form = build_default_filing_form();
          this.matter_form = build_default_matter_form();
          this.note_form = build_default_note_form();
          this.further_action_form = build_default_further_action_form();
          this.document_form = build_default_document_form();
          this.reset_case_workflow_actions();
        } else {
          this.show_action_error(error, fallback_message);
        }
      } finally {
        if (show_page_loader) {
          this.loading_case_details = false;
        }
      }
    },
    async move_case_to_next_status(transition) {
      if (this.transitioning_to_status_id !== null) {
        return;
      }

      if (!normalize_text(transition?.target)) {
        this.show_error('This workflow action is missing a target status. Please check the workflow transition setup.');
        return;
      }

      if (transition?.requiresHearingSchedule) {
        this.pending_case_transition = transition;
        this.hearing_schedule_form = build_default_hearing_schedule_form();
        this.show_schedule_hearing_modal = true;
        this.case_details_error = '';
        return;
      }

      await this.execute_case_transition(transition);
    },
    close_schedule_hearing_modal() {
      if (this.transitioning_to_status_id !== null) {
        return;
      }

      this.show_schedule_hearing_modal = false;
      this.pending_case_transition = null;
      this.hearing_schedule_form = build_default_hearing_schedule_form();
    },
    async confirm_schedule_hearing() {
      const hearing_date = normalize_text(this.hearing_schedule_form.hearing_date);
      if (!this.pending_case_transition) {
        return;
      }

      if (!hearing_date) {
        this.show_error('Please choose a hearing date.');
        return;
      }

      const transition_saved = await this.execute_case_transition(this.pending_case_transition, {
        hearing_date,
        hearing_notes: normalize_text(this.hearing_schedule_form.notes),
      });

      if (transition_saved) {
        this.close_schedule_hearing_modal();
      }
    },
    build_case_transition_payload(application_id, transition) {
      const payload = {
        application_id,
        status: transition.target,
        note: `${transition.action}: ${transition.source} -> ${transition.target}`,
        action_taken: transition.action,
      };

      if (transition.targetId) {
        payload.workflow_form_status_id = Number(transition.targetId);
      }

      if (transition.targetWorkflowStatusId) {
        payload.workflow_status_id = Number(transition.targetWorkflowStatusId);
      }

      if (this.case_workflow_context.entity_profile_id) {
        payload.entity_profile_id = Number(this.case_workflow_context.entity_profile_id);
      }

      return payload;
    },
    get_current_user_id() {
      return Number(
        sessionStorage.getItem('current_user_id')
        || sessionStorage.getItem('user_id')
        || 0
      );
    },
    async create_hearing_transition_journal(application_id, transition = {}, options = {}) {
      const hearing_date = normalize_text(options?.hearing_date);
      const current_user_id = this.get_current_user_id();
      if (!hearing_date || !current_user_id) {
        return;
      }

      await create_land_court_journal({
        application_id,
        user_id: current_user_id,
        journal_date: hearing_date,
        journal_description: serialize_matter_payload({
          title: normalize_text(transition?.action) || 'Status transition',
          status: 'Pending',
          description: normalize_text(options?.hearing_notes)
            ? `Hearing date: ${hearing_date}. ${normalize_text(options.hearing_notes)}`
            : `Hearing date: ${hearing_date}`,
        }),
      });
    },
    resolve_transition_application_id() {
      return Number(this.case_details?.application_id || this.$route?.params?.applicationId || 0);
    },
    persist_transition_case_meta(application_id, transition, options = {}) {
      const normalized_application_id = Number(application_id || 0);
      if (!normalized_application_id) {
        return;
      }

      const case_meta_by_application_id = parse_saved_case_meta();
      const current_meta = case_meta_by_application_id[String(normalized_application_id)] || {};
      const next_meta = {
        ...current_meta,
        application_id: normalized_application_id,
        status: normalize_text(transition?.target) || current_meta.status || '',
        workflow_form_status_id: Number(transition?.targetId || current_meta.workflow_form_status_id || 0) || null,
        workflow_status_id: Number(transition?.targetWorkflowStatusId || current_meta.workflow_status_id || 0) || null,
      };
      const hearing_date = normalize_text(options?.hearing_date);

      if (hearing_date) {
        next_meta.hearing_date = hearing_date;
      }

      persist_case_meta({
        ...case_meta_by_application_id,
        [String(normalized_application_id)]: next_meta,
      });
    },
    async finalize_case_transition(application_id, transition, options = {}) {
      const response = await verify_land_court_application(
        this.build_case_transition_payload(application_id, transition)
      );
      if (String(response?.status || '').toLowerCase() === 'error') {
        throw new Error(response?.message || 'Failed to move this case to the next status.');
      }

      this.persist_transition_case_meta(application_id, transition, options);
      this.apply_current_case_status(transition.target, options?.hearing_date);
      clear_caseload_snapshots();
      await this.refresh_case_details();
    },
    begin_case_transition(transition) {
      this.transitioning_to_status_id = Number(transition?.targetId || transition?.id || 0);
      this.case_details_error = '';
    },
    finish_case_transition() {
      this.transitioning_to_status_id = null;
    },
    set_case_transition_error(error) {
      this.show_action_error(error, 'Failed to move this case to the next status.');
    },
    async execute_case_transition(transition, options = {}) {
      if (this.transitioning_to_status_id !== null) {
        return;
      }

      if (!normalize_text(transition?.target)) {
        this.show_error('This workflow action is missing a target status. Please check the workflow transition setup.');
        return false;
      }

      const application_id = this.resolve_transition_application_id();
      if (!application_id) {
        this.show_error('No application id was supplied for this case.');
        return false;
      }

      this.begin_case_transition(transition);

      try {
        await this.create_hearing_transition_journal(application_id, transition, options);
        await this.finalize_case_transition(application_id, transition, options);
        this.show_success('Case status updated successfully.');
        return true;
      } catch (error) {
        this.set_case_transition_error(error);
        return false;
      } finally {
        this.finish_case_transition();
      }
    },
    begin_case_data_edit() {
      this.editing_case_data = true;
      this.case_data_form = {
        ...build_default_case_data_form(),
        ...(this.case_details.case_data_form || {}),
      };
    },
    cancel_case_data_edit() {
      this.editing_case_data = false;
      this.case_data_form = {
        ...build_default_case_data_form(),
        ...(this.case_details.case_data_form || {}),
      };
    },
    async save_case_data() {
      const payload_entries = CASE_DATA_FIELD_KEYS
        .map((field_key) => {
          const question_entry = read_case_data_question_entry(this.case_data_question_map, field_key);
          const answer_text = normalize_text(read_case_data_field_value(this.case_data_form, field_key));

          if (!question_entry?.application_form_id || !question_entry?.section_question_id || !answer_text) {
            return null;
          }

          return {
            field_key,
            application_form_id: Number(question_entry.application_form_id),
            section_question_id: Number(question_entry.section_question_id),
            section_index: Number(question_entry.section_index || 0),
            answer_text,
          };
        })
        .filter(Boolean);

      if (!payload_entries.length) {
        this.show_error('No backend-linked case data fields were found for this case.');
        return;
      }

      this.saving_case_data = true;
      this.case_details_error = '';

      try {
        const grouped_payloads = group_payload_entries_by_application_form(payload_entries);

        await Promise.all(
          Object.values(grouped_payloads).map((payload) => submit_land_court_form_answers(payload))
        );

        this.editing_case_data = false;
        clear_caseload_snapshots();
        await this.refresh_case_details();
        this.show_success('Case data saved successfully.');
      } catch (error) {
        this.show_action_error(error, 'Failed to save case data changes.');
      } finally {
        this.saving_case_data = false;
      }
    },
    open_parties_modal() {
      this.show_parties_modal = true;
      this.parties_form = build_parties_form(this.case_details.parties_form || {});
    },
    close_parties_modal() {
      if (this.saving_parties) {
        return;
      }
      this.show_parties_modal = false;
      this.parties_form = build_parties_form(this.case_details.parties_form || {});
    },
    open_add_parties_modal() {
      this.show_add_parties_modal = true;
      this.add_parties_form = build_default_parties_form();
    },
    close_add_parties_modal() {
      if (this.saving_parties) {
        return;
      }
      this.show_add_parties_modal = false;
      this.add_parties_form = build_default_parties_form();
    },
    async persist_parties(form_values = {}, options = {}) {
      const payload_entries = build_parties_payload_entries(
        this.parties_question_map,
        this.case_details?.parties,
        form_values,
        options
      );

      if (!payload_entries.length) {
        this.show_error('No editable backend-backed party fields were found for this case.');
        return;
      }

      this.saving_parties = true;
      this.case_details_error = '';

      try {
        const grouped_payloads = group_payload_entries_by_application_form(payload_entries);
        const party_payloads = Object.values(grouped_payloads);

        const save_responses = await Promise.all(
          party_payloads.map(async (payload) => ({
            payload,
            response: await submit_land_court_form_answers(payload),
          }))
        );
        const has_incomplete_save = save_responses.some(({ payload, response }) => {
          const saved_answers = Array.isArray(response?.saved_answers) ? response.saved_answers : [];
          return saved_answers.length < (payload?.answers || []).length;
        });

        if (has_incomplete_save) {
          this.show_error('Did not save every party and representative answer.');
        }

        this.show_parties_modal = false;
        this.show_add_parties_modal = false;
        this.add_parties_form = build_default_parties_form();
        clear_caseload_snapshots();
        await this.refresh_case_details();
        if (!has_incomplete_save) {
          this.show_success(
            options?.append_mode
              ? 'Parties and representatives added successfully.'
              : 'Parties and representatives saved successfully.'
          );
        }
      } catch (error) {
        this.show_action_error(error, 'Failed to save parties and representation changes.');
      } finally {
        this.saving_parties = false;
      }
    },
    async save_parties() {
      await this.persist_parties(this.parties_form, { append_mode: false });
    },
    async save_added_parties() {
      await this.persist_parties(this.add_parties_form, { append_mode: true });
    },
    get_journal_form_config(journal_type) {
      return journal_form_config(journal_type);
    },
    open_add_journal(journal_type) {
      const config = this.get_journal_form_config(journal_type);
      if (!config) {
        return;
      }

      set_journal_add_visible(this, journal_type, true);
      set_journal_edit_visible(this, journal_type, false);
      set_journal_editing_id(this, journal_type, null);
      set_journal_form(this, journal_type, config.build_default_form());
    },
    cancel_journal(journal_type) {
      const config = this.get_journal_form_config(journal_type);
      if (!config || journal_is_saving(this, journal_type)) {
        return;
      }

      set_journal_add_visible(this, journal_type, false);
      set_journal_edit_visible(this, journal_type, false);
      set_journal_editing_id(this, journal_type, null);
      set_journal_form(this, journal_type, config.build_default_form());
    },
    edit_journal(journal_type, item = {}) {
      const config = this.get_journal_form_config(journal_type);
      if (!config) {
        return;
      }

      this.case_details_error = '';
      set_journal_add_visible(this, journal_type, false);
      set_journal_edit_visible(this, journal_type, true);
      set_journal_editing_id(this, journal_type, Number(item?.id || 0) || null);
      set_journal_form(this, journal_type, config.build_form_from_item(item));
    },
    async save_journal(journal_type) {
      const config = this.get_journal_form_config(journal_type);
      if (!config) {
        return;
      }

      const form_values = journal_form(this, journal_type);
      const application_id = Number(this.$route?.params?.applicationId || 0);
      const user_id = current_user_id();
      const validation_message = journal_validation_message(
        config,
        journal_type,
        form_values,
        application_id,
        user_id
      );

      if (validation_message) {
        this.show_error(validation_message);
        return;
      }

      set_journal_saving(this, journal_type, true);
      this.case_details_error = '';

      try {
        await persist_journal_entry(config, journal_type, form_values, {
          application_id,
          user_id,
          editing_id: journal_editing_id(this, journal_type),
        });

        reset_journal_editor(this, journal_type, config);
        clear_caseload_snapshots();
        await this.refresh_case_details();
        this.show_success(`${humanize_field_key(journal_type)} saved successfully.`);
      } catch (error) {
        this.show_action_error(error, config.failure_message);
      } finally {
        set_journal_saving(this, journal_type, false);
      }
    },
    open_add_filing() {
      this.open_add_journal('filing');
    },
    cancel_add_filing() {
      this.cancel_journal('filing');
    },
    cancel_filing_edit() {
      if (this.editing_backend_filing_section_key) {
        this.cancel_backend_filing();
        return;
      }

      this.cancel_add_filing();
    },
    edit_filing(item = {}) {
      this.edit_journal('filing', item);
    },
    async save_filing() {
      await this.save_journal('filing');
    },
    async save_filing_edit() {
      if (this.editing_backend_filing_section_key) {
        const section = this.backend_case_sections.find(
          (section_item) => section_item.backend_key === this.editing_backend_filing_section_key
        );
        if (section) {
          await this.save_backend_filing(section);
          return;
        }
      }

      await this.save_filing();
    },
    filing_actor_options() {
      return FILING_ACTOR_OPTIONS;
    },
    open_add_matter() {
      this.open_add_journal('matter');
    },
    cancel_add_matter() {
      this.cancel_journal('matter');
    },
    cancel_matter_edit() {
      if (this.editing_backend_matter_section_key) {
        this.cancel_backend_matter();
        return;
      }

      this.cancel_add_matter();
    },
    edit_matter(item = {}) {
      this.edit_journal('matter', item);
    },
    async save_matter() {
      await this.save_journal('matter');
    },
    async save_matter_edit() {
      if (this.editing_backend_matter_section_key) {
        const section = this.backend_case_sections.find(
          (section_item) => section_item.backend_key === this.editing_backend_matter_section_key
        );
        if (section) {
          await this.save_backend_matter(section);
          return;
        }
      }

      await this.save_matter();
    },
    matter_status_options() {
      return MATTER_STATUS_OPTIONS;
    },
    is_updating_matter_status(item = {}) {
      return this.updating_matter_status_id === Number(item?.id || 0);
    },
    async update_matter_status(item = {}, next_status = '') {
      const journal_id = Number(item?.id || 0);
      const normalized_next_status = normalize_matter_status(next_status);

      if (!should_update_matter_status(item, normalized_next_status)) {
        return;
      }

      this.updating_matter_status_id = journal_id;
      this.case_details_error = '';

      try {
        await update_land_court_journal(matter_status_update_payload(item, normalized_next_status));

        clear_caseload_snapshots();
        await this.refresh_case_details();
        this.show_success('Matter status updated successfully.');
      } catch (error) {
        this.show_action_error(error, 'Failed to update the matter status.');
      } finally {
        this.updating_matter_status_id = null;
      }
    },
    open_add_further_action() {
      this.open_add_journal('further_action');
    },
    cancel_add_further_action() {
      this.cancel_journal('further_action');
    },
    edit_further_action(item = {}) {
      this.edit_journal('further_action', item);
    },
    async save_further_action() {
      this.apply_further_action_status_defaults();
      await this.save_journal('further_action');
    },
    further_action_status_options() {
      return FURTHER_ACTION_STATUS_OPTIONS;
    },
    apply_further_action_status_defaults() {
      const status = normalize_further_action_status(this.further_action_form?.status);
      this.further_action_form = {
        ...this.further_action_form,
        status,
        reminder_at: reminder_value_for_further_action_status(status, this.further_action_form?.reminder_at),
      };
    },
    handle_further_action_status_change() {
      this.apply_further_action_status_defaults();
    },
    is_updating_further_action_status(item = {}) {
      return this.updating_further_action_status_id === Number(item?.id || 0);
    },
    async update_further_action_status(item = {}, next_status = '') {
      const journal_id = Number(item?.id || 0);
      const normalized_next_status = normalize_further_action_status(next_status);

      if (!should_update_further_action_status(item, normalized_next_status)) {
        return;
      }

      this.updating_further_action_status_id = journal_id;
      this.case_details_error = '';

      try {
        await update_land_court_journal(further_action_status_update_payload(item, normalized_next_status));

        clear_caseload_snapshots();
        await this.refresh_case_details();
        this.show_success('Further action status updated successfully.');
      } catch (error) {
        this.show_action_error(error, 'Failed to update the further action status.');
      } finally {
        this.updating_further_action_status_id = null;
      }
    },
    open_add_note() {
      this.open_add_journal('note');
    },
    cancel_add_note() {
      this.cancel_journal('note');
    },
    cancel_note_edit() {
      if (this.editing_backend_note_section_key) {
        this.cancel_backend_note();
        return;
      }

      this.cancel_add_note();
    },
    edit_note(item = {}) {
      this.edit_journal('note', item);
    },
    async save_note() {
      await this.save_journal('note');
    },
    async save_note_edit() {
      if (this.editing_backend_note_section_key) {
        const section = this.backend_case_sections.find(
          (section_item) => section_item.backend_key === this.editing_backend_note_section_key
        );
        if (section) {
          await this.save_backend_note(section);
          return;
        }
      }

      await this.save_note();
    },
    open_add_document() {
      this.showing_add_document = true;
      this.document_form = build_default_document_form();
    },
    cancel_add_document() {
      if (this.saving_document) {
        return;
      }
      this.showing_add_document = false;
      this.document_form = build_default_document_form();
      if (this.$refs.document_file_input) {
        this.$refs.document_file_input.value = '';
      }
    },
    trigger_document_picker() {
      this.$refs.document_file_input?.click();
    },
    handle_document_file_change(event) {
      const selected_files = Array.from(event?.target?.files || []);
      if (!selected_files.length) {
        this.document_form.file = null;
        this.document_form.files = [];
        this.document_form.file_name = '';
        this.document_form.file_size_label = '';
        return;
      }

      const invalid_file = selected_files.find((selected_file) => {
        const extension = selected_file.name.split('.').pop()?.toLowerCase() || '';
        return !ALLOWED_DOCUMENT_EXTENSIONS.includes(extension);
      });

      if (invalid_file) {
        this.show_error('Only PDF, DOC, DOCX, and JPG files are supported for case documents.');
        this.document_form.file = null;
        this.document_form.files = [];
        this.document_form.file_name = '';
        this.document_form.file_size_label = '';
        event.target.value = '';
        return;
      }

      const oversized_file = selected_files.find((selected_file) => selected_file.size > MAX_DOCUMENT_SIZE_BYTES);
      if (oversized_file) {
        this.show_error('Documents must be 50 MB or smaller.');
        this.document_form.file = null;
        this.document_form.files = [];
        this.document_form.file_name = '';
        this.document_form.file_size_label = '';
        event.target.value = '';
        return;
      }

      this.case_details_error = '';
      this.document_form.file = selected_files[0];
      this.document_form.files = selected_files;
      this.document_form.file_name = selected_files.length === 1
        ? selected_files[0].name
        : `${selected_files.length} documents selected`;
      this.document_form.file_size_label = selected_files.length === 1
        ? format_file_size(selected_files[0].size)
        : selected_files.map((selected_file) => format_file_size(selected_file.size)).join(', ');
    },
    async save_document() {
      const application_id = Number(this.$route?.params?.applicationId || 0);

      if (!application_id) {
        this.show_error('Missing application context for saving a document.');
        return;
      }

      const selected_files = this.document_form.files?.length
        ? this.document_form.files
        : [this.document_form.file].filter(Boolean);

      if (!selected_files.length) {
        this.show_error('Please choose a document to upload.');
        return;
      }

      this.saving_document = true;
      this.case_details_error = '';

      try {
        const payload = new FormData();
        payload.append('application_id', String(application_id));
        selected_files.forEach((selected_file) => {
          payload.append('files', selected_file);
          payload.append('document_names', selected_file.name);
        });

        await upload_land_court_additional_documents(payload);

        selected_files.forEach((selected_file) => {
          persist_document_display_meta(application_id, {
            source: 'additional',
            file_name: selected_file.name,
            category: this.document_form.category,
            party: this.document_form.party,
            date: this.document_form.document_date,
            size: format_file_size(selected_file.size),
          });
        });

        this.document_form = build_default_document_form();
        if (this.$refs.document_file_input) {
          this.$refs.document_file_input.value = '';
        }
        clear_caseload_snapshots();
        await this.refresh_case_details();
        this.show_success(selected_files.length === 1
          ? 'Document uploaded successfully.'
          : 'Documents uploaded successfully.');
      } catch (error) {
        this.show_action_error(error, 'Failed to upload the document.');
      } finally {
        this.saving_document = false;
      }
    },
    remove_document(document) {
      const application_id = Number(this.$route?.params?.applicationId || 0);
      const hidden_key = normalize_text(document?.hidden_storage_key);

      if (!application_id || !hidden_key) {
        return;
      }

      persist_hidden_document_key(application_id, hidden_key);
      this.case_details = {
        ...this.case_details,
        documents: (this.case_details.documents || []).filter(
          (item) => item.hidden_storage_key !== hidden_key
        ),
      };
    },
    document_category_options() {
      return DOCUMENT_CATEGORY_OPTIONS;
    },
    document_party_options() {
      return DOCUMENT_PARTY_OPTIONS;
    },
    case_data_field_options(field_key) {
      const question_entry = read_case_data_question_entry(this.case_data_question_map, field_key);
      const backend_options = question_entry?.options || [];
      const options = backend_options.length ? backend_options : (case_data_config(field_key).options || []);
      const current_value = normalize_case_data_field_value(
        field_key,
        read_case_data_field_value(this.case_data_form, field_key)
          || read_case_data_field_value(this.case_details?.case_data_form, field_key),
        question_entry
      );

      if (!current_value || options.some((option) => normalize_label(option) === normalize_label(current_value))) {
        return options;
      }

      return [current_value, ...options];
    },
    case_data_field_label(field = {}) {
      return normalize_text(field?.label) || humanize_field_key(field?.key);
    },
    case_data_field_placeholder(field = {}) {
      const label = this.case_data_field_label(field).toLowerCase();
      return field?.input_type === 'select' ? `Select ${label}` : `Enter ${label}`;
    },
    go_back_to_caseload() {
      this.$router.push({ name: 'LandCourtHome' });
    },
    list_from_multiline(value) {
      return normalize_text(value)
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);
    },
    representative_blocks(value) {
      return normalize_representative_detail_blocks(split_representative_text_blocks(value))
        .filter((item) => representative_detail_has_value(item));
    },
    representative_detail_rows(value) {
      return representative_detail_rows_from_block(value);
    },
    representative_verification_options() {
      return LEGAL_REPRESENTATIVE_VERIFICATION_OPTIONS;
    },
    representative_verification_blocks(value, representative_count = 1) {
      return normalize_verification_blocks(split_text_blocks(value), Array.from({ length: representative_count }, () => 'Representative'));
    },
    representative_verification_label(value) {
      return normalize_legal_representative_verification(value);
    },
    representative_verification_class(value) {
      return {
        'land-court-verification-verified': normalize_legal_representative_verification(value) === 'Verified',
        'land-court-verification-unverified': normalize_legal_representative_verification(value) !== 'Verified',
      };
    },
    add_parties_form_block(form_key, next_value = build_empty_representative_detail()) {
      this.parties_form = update_repeatable_party_blocks(
        this.parties_form,
        form_key,
        (next_blocks) => [...next_blocks, next_value]
      );

      const verification_key = representative_verification_field_key(form_key);
      if (verification_key) {
        this.parties_form = update_repeatable_party_blocks(
          this.parties_form,
          verification_key,
          (next_blocks) => [...next_blocks, DEFAULT_LEGAL_REPRESENTATIVE_VERIFICATION]
        );
      }
    },
    remove_parties_form_block(form_key, index) {
      this.parties_form = update_repeatable_party_blocks(
        this.parties_form,
        form_key,
        (next_blocks) => {
          next_blocks.splice(index, 1);
          return next_blocks.length ? next_blocks : [build_empty_representative_detail()];
        }
      );

      const verification_key = representative_verification_field_key(form_key);
      if (verification_key) {
        this.parties_form = update_repeatable_party_blocks(
          this.parties_form,
          verification_key,
          (next_blocks) => {
            next_blocks.splice(index, 1);
            return next_blocks.length ? next_blocks : [DEFAULT_LEGAL_REPRESENTATIVE_VERIFICATION];
          }
        );
      }
    },
    add_additional_parties_form_block(form_key, next_value = build_empty_representative_detail()) {
      this.add_parties_form = update_repeatable_party_blocks(
        this.add_parties_form,
        form_key,
        (next_blocks) => [...next_blocks, next_value]
      );

      const verification_key = representative_verification_field_key(form_key);
      if (verification_key) {
        this.add_parties_form = update_repeatable_party_blocks(
          this.add_parties_form,
          verification_key,
          (next_blocks) => [...next_blocks, DEFAULT_LEGAL_REPRESENTATIVE_VERIFICATION]
        );
      }
    },
    remove_additional_parties_form_block(form_key, index) {
      this.add_parties_form = update_repeatable_party_blocks(
        this.add_parties_form,
        form_key,
        (next_blocks) => {
          next_blocks.splice(index, 1);
          return next_blocks.length ? next_blocks : [build_empty_representative_detail()];
        }
      );

      const verification_key = representative_verification_field_key(form_key);
      if (verification_key) {
        this.add_parties_form = update_repeatable_party_blocks(
          this.add_parties_form,
          verification_key,
          (next_blocks) => {
            next_blocks.splice(index, 1);
            return next_blocks.length ? next_blocks : [DEFAULT_LEGAL_REPRESENTATIVE_VERIFICATION];
          }
        );
      }
    },
    section_chip_class(variant) {
      return {
        'land-court-status-warning': variant === 'warning' || variant === 'respondent',
        'land-court-status-success': variant === 'success' || variant === 'applicant',
        'land-court-status-hearing': variant === 'hearing' || variant === 'court',
        'land-court-neutral-chip': !['warning', 'respondent', 'success', 'applicant', 'hearing', 'court'].includes(variant),
      };
    },
    document_extension_class(extension_variant_value) {
      return {
        'land-court-doc-badge-pdf': extension_variant_value === 'pdf',
        'land-court-doc-badge-docx': extension_variant_value === 'docx',
        'land-court-neutral-chip': extension_variant_value === 'file',
      };
    },
    resolve_document_url(document = {}) {
      return first_non_empty_value([
        document?.preview_url,
        document?.document_url,
        document?.presigned_url,
        document?.url,
        document?.file_url,
      ]);
    },
    close_document_preview() {
      this.selected_document = null;
    },
    view_document(document) {
      const url = this.resolve_document_url(document);
      if (!url) {
        this.show_error('No preview URL is available for this document.');
        return;
      }

      this.selected_document = {
        ...document,
        preview_url: url,
      };
    },
    download_document(document) {
      const url = this.resolve_document_url(document);
      if (!url || typeof window === 'undefined') {
        this.show_error('No download URL is available for this document.');
        return;
      }

      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download = normalize_text(document?.name) || 'document';
      anchor.rel = 'noopener';
      window.document.body.appendChild(anchor);
      anchor.click();
      window.document.body.removeChild(anchor);
    },
  },
};
